  import React, { Component } from "react";
  import { Link } from "react-router-dom";
  import gql from "graphql-tag";
  import { graphql, withApollo } from "react-apollo";
  import * as compose from "lodash.flowright";
  import * as Cookies from "es-cookie";
  import { scopeBranches, getEffectiveBranchIds, onBranchScopeChange } from "../../../utils/branchScope";
  import { canView } from "../../../utils/modulePermissions";
  import { attachModuleGuard, AccessDeniedCard, AccessDeniedModal } from "../../../utils/moduleGuard";

  // /dashboard → "Dashboard" (see MODULE_ROUTES in moduleAccessGuard). Match on module_key
  // only, never module_name. is_view === 0 blocks the page + its data. is_edit === 0 blocks
  // navigation OUT of the dashboard: every KPI card / "Full report" link is intercepted on
  // click and shows Access Denied instead of redirecting (see handleNavGuardCapture).
  const DASHBOARD_MODULE = ["Dashboard"];

  // -----------------------------------------------------------------------------
  // GraphQL Queries
  // -----------------------------------------------------------------------------
  const FETCH_BRANCHES = gql`
    query getBranches($institution_id: Int!) {
      getBranches(institution_id: $institution_id) {
        id
        branch_name
      }
    }
  `;

  const GET_COMBINED_DASHBOARD_DATA = gql`
    query getCombinedDashboardData(
      $instituteId: Int!,
      $institutionId: Int!,
      $date: String!,
      $limit: Int,
      $months: Int!,
      $month: Int!,
      $year: Int!,
      $branchIds: [Int]
    ) {
      getDashboardTotals(institutionId: $institutionId, branchIds: $branchIds) {
        totalStudents
        newAdmissions
        pendingLeads
        todayMessagesCount
        totalExamPapers
        expiredExamPapers
      }
      getDashboardFeeOverview(instituteId: $instituteId, branchIds: $branchIds) {
        summary {
          totalAmount
          paidAmount
          pendingAmount
          overdueAmount
          collectionPercentage
        }
        termWiseData {
          termNo
          termName
          totalExpected
          totalCollected
          totalPending
          totalOverdue
          collectionPercentage
          totalStudents
          paidStudents
          pendingStudents
          overdueStudents
        }
      }
      getAttendanceTodayByDate(instituteId: $instituteId, date: $date, branchIds: $branchIds) {
        date
        studentAttendance {
          present
          absent
          late
          totalStudents
          presentPercentage
        }
        classWiseAttendance {
          classId
          className
          present
          total
          percentage
        }
        staffAttendance {
          teaching
          nonTeaching
          absent
          totalStaff
          presentPercentage
        }
      }
      getRecentExamResults(institutionId: $institutionId, limit: $limit, branchIds: $branchIds) {
        exams {
          examId
          examName
          examDate
          totalStudents
          passRate
          averageScore
          highestScore
          lowestScore
          passCount
          failCount
        }
        summary {
          averagePassRate
          examsThisMonth
          topScorerName
          topScorerScore
        }
      }
      getLastExamAverageBySubject(institutionId: $institutionId, branchIds: $branchIds) {
        overallAverage
        subjects {
          subjectName
          averageScore
          accuracy
        }
      }
      getMonthlyEnrollments(instituteId: $instituteId, months: $months, branchIds: $branchIds) {
        totalEnrollments
        currentMonthCount
        monthlyData {
          month
          count
          percentage
        }
      }
      getAcademicPerformanceByGrade(instituteId: $instituteId, branchIds: $branchIds) {
        gradePerformance {
          classId
          gradeName
          totalStudents
          passedStudents
          passPercentage
          avgScore
        }
        examTrend {
          examName
          examDate
          avgScore
          passRate
        }
        overallPassRate
        weakestGrade
        topGrade
      }
      getAdmissionFunnel(instituteId: $instituteId, branchIds: $branchIds) {
        totalLeads
        totalInquiries
        totalVisited
        totalApplied
        totalEnrolled
        overallConversionRate
        avgDaysToEnroll
      }
      getRevenueOverview(instituteId: $instituteId, month: $month, year: $year, branchIds: $branchIds) {
        totalCollected
        totalOutstanding
        netRevenue
        receiptCount
        dailyData {
          date
          amount
          receiptCount
        }
        categoryBreakdown {
          category
          amount
          percentage
        }
      }
      getTodayAtGlance(instituteId: $instituteId, branchIds: $branchIds) {
        date
        classStats {
          totalClasses
          classesOnTime
          classesSubstituted
          onTimePercentage
        }
        feeStats {
          amountCollected
          receiptCount
        }
        openTasksCount
        todayExamsCount
        upcomingExamsCount
        todayExamNames
      }
    }
  `;

  // -----------------------------------------------------------------------------
  // Helpers
  // -----------------------------------------------------------------------------
  const MONTH_SHORT = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  // Format number as Indian currency short form  (e.g. 1842600 -> ₹18.4 L)
  const fmtINR = (v) => {
    if (!v && v !== 0) return "--";
    const n = Number(v);
    if (n >= 10000000) return `₹${(n / 10000000).toFixed(1)} Cr`;
    if (n >= 100000) return `₹${(n / 100000).toFixed(1)} L`;
    if (n >= 1000) return `₹${(n / 1000).toFixed(1)} K`;
    return `₹${n.toLocaleString("en-IN")}`;
  };

  // Full Indian number format (e.g. 1842600 -> ₹18,42,600)
  const fmtINRFull = (v) => {
    if (!v && v !== 0) return "₹0";
    return `₹${Number(v).toLocaleString("en-IN")}`;
  };

  // Pick bar / status color by percentage
  const pctColor = (p) => (p >= 80 ? "#22c55e" : p >= 60 ? "#f59e0b" : "#ef4444");

  // Skeleton placeholder
  const Sk = ({ w = "100%", h = 14, r = 4 }) => (
    <div
      style={{
        width: w,
        height: h,
        borderRadius: r,
        // Moving gradient "sweep" reads clearly as loading on a slow dashboard —
        // a light band travels across the grey block (see dsh-shimmer keyframes).
        background:
          "linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 37%, #e5e7eb 63%)",
        backgroundSize: "400% 100%",
        animation: "dsh-shimmer 1.4s ease-in-out infinite",
      }}
    />
  );

  // Pure SVG Sparkline trend chart with smooth bezier curves and drop-shadow glow
  const Sparkline = ({ data = [30, 45, 35, 60, 49, 70, 65], color = "#3b82f6" }) => {
    if (!data || data.length === 0) return null;
    const width = 85;
    const height = 28;
    const max = Math.max(...data);
    const min = Math.min(...data);
    const range = max - min === 0 ? 1 : max - min;

    const coords = data.map((val, index) => {
      const x = (index / (data.length - 1)) * width;
      const y = height - ((val - min) / range) * (height - 6) - 3;
      return { x, y };
    });

    const getBezierPath = (pts) => {
      if (pts.length === 0) return "";
      let dPath = `M ${pts[0].x} ${pts[0].y}`;
      for (let i = 0; i < pts.length - 1; i++) {
        const p0 = pts[i];
        const p1 = pts[i + 1];
        const cp1x = p0.x + (p1.x - p0.x) / 2;
        const cp1y = p0.y;
        const cp2x = p1.x - (p1.x - p0.x) / 2;
        const cp2y = p1.y;
        dPath += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p1.x} ${p1.y}`;
      }
      return dPath;
    };

    const lineD = getBezierPath(coords);
    const fillD = lineD ? `${lineD} L ${width} ${height} L 0 ${height} Z` : "";
    const gradId = `grad-${color.replace("#", "")}`;
    const filterId = `glow-${color.replace("#", "")}`;

    return (
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={{ overflow: "visible" }}>
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.25" />
            <stop offset="100%" stopColor={color} stopOpacity="0.0" />
          </linearGradient>
          <filter id={filterId} x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="1.5" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        {fillD && <path d={fillD} fill={`url(#${gradId})`} />}
        {lineD && (
          <>
            <path
              d={lineD}
              fill="none"
              stroke={color}
              strokeWidth="3.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              opacity="0.3"
              filter={`url(#${filterId})`}
            />
            <path
              d={lineD}
              fill="none"
              stroke={color}
              strokeWidth="1.75"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </>
        )}
      </svg>
    );
  };

  // Pure SVG Circular Progress Gauge with linear gradients and active mount animations
  const CircularGauge = ({ percentage = 0, size = 52, strokeWidth = 5, color = "#3b82f6" }) => {
    const [currentPct, setCurrentPct] = React.useState(0);

    React.useEffect(() => {
      const t = setTimeout(() => {
        setCurrentPct(percentage);
      }, 100);
      return () => clearTimeout(t);
    }, [percentage]);

    const radius = (size - strokeWidth) / 2;
    const circumference = radius * 2 * Math.PI;
    const pct = Math.max(0, Math.min(100, currentPct));
    const strokeDashoffset = circumference - (pct / 100) * circumference;

    const getGradientColors = (baseColor) => {
      switch (baseColor.toLowerCase()) {
        case "#16a34a": return ["#16a34a", "#4ade80"];
        case "#14b8a6": return ["#14b8a6", "#2dd4bf"];
        case "#f59e0b": return ["#f59e0b", "#fde047"];
        case "#3b82f6": return ["#3b82f6", "#60a5fa"];
        case "#22c55e": return ["#22c55e", "#86efac"];
        default: return [baseColor, baseColor];
      }
    };

    const [startColor, endColor] = getGradientColors(color);
    const gradId = `gauge-grad-${color.replace("#", "")}`;

    return (
      <div style={{ position: "relative", width: size, height: size, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: "rotate(-90deg)", overflow: "visible" }}>
          <defs>
            <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor={startColor} />
              <stop offset="100%" stopColor={endColor} />
            </linearGradient>
          </defs>
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="rgba(0,0,0,0.05)"
            strokeWidth={strokeWidth}
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={`url(#${gradId})`}
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            style={{
              transition: "stroke-dashoffset 0.8s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          />
        </svg>
        <div style={{ position: "absolute", fontSize: 11, fontWeight: 700, color: "#111827" }}>
          {Math.round(pct)}%
        </div>
      </div>
    );
  };


  const card = {
    position: "relative",
    background: "rgba(255, 255, 255, 0.8)",
    backdropFilter: "blur(20px)",
    WebkitBackdropFilter: "blur(20px)",
    borderRadius: 16,
    border: "1px solid rgba(255, 255, 255, 0.6)",
    boxShadow: "0 10px 30px -12px rgba(0, 0, 0, 0.03), 0 1px 2px rgba(0, 0, 0, 0.01)",
    overflow: "hidden",
    transition: "transform 0.3s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.3s cubic-bezier(0.16, 1, 0.3, 1), border-color 0.3s ease",
  };

  const lbl = {
    fontSize: 10,
    fontWeight: 700,
    color: "#9ca3af",
    letterSpacing: "0.07em",
    textTransform: "uppercase",
  };

  const OutlineBtn = ({ to, children }) => (
    <Link
      to={to}
      style={{
        fontSize: 12,
        color: "#374151",
        textDecoration: "none",
        border: "1px solid #e5e7eb",
        borderRadius: 6,
        padding: "4px 12px",
        fontWeight: 500,
      }}
    >
      {children}
    </Link>
  );

  // -----------------------------------------------------------------------------
  // Component
  // -----------------------------------------------------------------------------
  class NewSchoolDashboardSection extends Component {
    state = {
      totalsData: null,
      feeData: null,
      attendanceData: null,
      examResults: [],
      examSummary: null,
      subjectScores: [],
      overallAverage: null,
      whatsappData: null,
      enrollmentData: null,
      modulesData: null,
      // Phase 2 data
      academicPerformance: null,
      seatCapacity: null,
      topDefaulters: null,
      admissionFunnel: null,
      revenueOverview: null,
      todayAtGlance: null,
      parentEngagement: null,
      // loading flags
      loadingFee: true,
      loadingAttendance: true,
      loadingExams: true,
      loadingSubjects: true,
      loadingWhatsApp: true,
      loadingEnrollment: true,
      loadingModules: true,
      loadingAcademic: true,
      loadingSeatCapacity: true,
      loadingTopDefaulters: true,
      loadingAdmissionFunnel: true,
      loadingRevenueOverview: true,
      loadingTodayAtGlance: true,
      loadingParentEngagement: true,
      showSeatModal: false,

      // Branches
      branches: [],
      selectedBranchIds: [],
      loadingBranches: true,
      isDrawerOpen: false,

      // Flipped to true by the guard when a role without is_edit clicks a nav card/link.
      accessDeniedShow: false,
    };

    // Buttons/links stay VISIBLE; handleNavGuardCapture intercepts the click and opens the
    // Access Denied modal (blocking the redirect) when the role lacks is_edit.
    guard = attachModuleGuard(this, DASHBOARD_MODULE);

    // Capture-phase click handler on the page container. react-router v5 Link only calls
    // history.push when event.defaultPrevented is false, so preventing the default here (on
    // any <a>) cancels the navigation. One handler covers every nav link/card at once.
    handleNavGuardCapture = (e) => {
      const anchor = e.target.closest && e.target.closest("a");
      if (!anchor) return;                 // not a navigation click — let it through
      if (!this.guard.requireEdit()) {     // pops Access Denied, returns false
        e.preventDefault();
      }
    };

    componentDidMount = async () => {
      // is_view === 0 → don't fetch branches or the combined dashboard payload. Both are
      // imperative client.query() calls, so bailing here keeps the data off the wire;
      // render() returns the Access Denied card.
      if (!canView(DASHBOARD_MODULE)) return;

      // Scope the dashboard from the start, so the first fetch doesn't send an
      // empty branchIds (which the backend treats as all branches).
      // getEffectiveBranchIds() is the header's (NavbarOne) selection, falling
      // back to every branch the login branch_id cookie authorizes.
      const scopedIds = getEffectiveBranchIds().map((id) => parseInt(id)).filter((id) => !isNaN(id));
      this.setState({
        selectedBranchIds: scopedIds,
      }, () => {
        this.fetchBranches();
        this.fetchDashboardData();
      });

      // Branch comes from the header — re-scope and refetch when it changes.
      this.offBranchScope = onBranchScopeChange(() => {
        const ids = getEffectiveBranchIds().map((id) => parseInt(id)).filter((id) => !isNaN(id));
        this.setState({ selectedBranchIds: ids }, () => {
          this.fetchBranches();
          this.fetchDashboardData();
        });
      });
    };

    componentWillUnmount() {
      if (this.offBranchScope) this.offBranchScope();
    }

    fetchBranches = async () => {
      const institutionId = parseInt(Cookies.get("institutionid")) || 0;

      try {
        const response = await this.props.client.query({
          query: FETCH_BRANCHES,
          variables: { institution_id: institutionId },
          fetchPolicy: "network-only"
        });
        if (response.data && response.data.getBranches) {
          // The header's branch selection IS this dashboard's branch filter.
          // scopeBranches() intersects it with the branches the logged-in user is
          // authorized for (the comma-separated "branch_id" cookie), so it can
          // never widen past them; an empty header selection means "all authorized".
          const branchList = scopeBranches(response.data.getBranches);

          const scopedIds = branchList
            .map((b) => parseInt(b.id))
            .filter((id) => !isNaN(id));

          this.setState({
            branches: branchList.map(b => ({ ...b, id: parseInt(b.id) })),
            selectedBranchIds: scopedIds,
            loadingBranches: false,
          });
        }
      } catch (err) {
        console.error("Failed to load branches list:", err);
        this.setState({ loadingBranches: false });
      }
    };

    fetchDashboardData = async () => {
      const id = parseInt(Cookies.get("institutionid")) || 0;
      const date = new Date().toISOString().split("T")[0];
      const now = new Date();
      const { selectedBranchIds } = this.state;

      this.setState({
        loadingFee: true,
        loadingAttendance: true,
        loadingExams: true,
        loadingSubjects: true,
        loadingWhatsApp: true,
        loadingEnrollment: true,
        loadingModules: true,
        loadingAcademic: true,
        loadingSeatCapacity: true,
        loadingTopDefaulters: true,
        loadingAdmissionFunnel: true,
        loadingRevenueOverview: true,
        loadingTodayAtGlance: true,
        loadingParentEngagement: true,
      });

      try {
        const response = await this.props.client.query({
          query: GET_COMBINED_DASHBOARD_DATA,
          variables: {
            instituteId: id,
            institutionId: id,
            date,
            limit: 5,
            months: 12,
            month: now.getMonth() + 1,
            year: now.getFullYear(),
            branchIds: selectedBranchIds && selectedBranchIds.length > 0
              ? selectedBranchIds.map(id => parseInt(id))
              : null,
          },
          fetchPolicy: "network-only"
        });

        const d = response.data;
        this.setState({
          totalsData: d.getDashboardTotals || null,
          feeData: d.getDashboardFeeOverview || null,
          attendanceData: d.getAttendanceTodayByDate || null,
          examResults: d.getRecentExamResults?.exams || [],
          examSummary: d.getRecentExamResults?.summary || null,
          subjectScores: d.getLastExamAverageBySubject?.subjects || [],
          overallAverage: d.getLastExamAverageBySubject?.overallAverage || null,
          whatsappData: d.getWhatsAppDashboard || null,
          enrollmentData: d.getMonthlyEnrollments || null,
          academicPerformance: d.getAcademicPerformanceByGrade || null,
          seatCapacity: d.getSeatCapacity || null,
          topDefaulters: d.getTopDefaulters || null,
          admissionFunnel: d.getAdmissionFunnel || null,
          revenueOverview: d.getRevenueOverview || null,
          todayAtGlance: d.getTodayAtGlance || null,
          parentEngagement: d.getParentEngagement || null,

          loadingFee: false,
          loadingAttendance: false,
          loadingExams: false,
          loadingSubjects: false,
          loadingWhatsApp: false,
          loadingEnrollment: false,
          loadingModules: false,
          loadingAcademic: false,
          loadingSeatCapacity: false,
          loadingTopDefaulters: false,
          loadingAdmissionFunnel: false,
          loadingRevenueOverview: false,
          loadingTodayAtGlance: false,
          loadingParentEngagement: false,
        });
      } catch (err) {
        console.error("Failed to load dashboard data:", err);
        this.setState({
          loadingFee: false,
          loadingAttendance: false,
          loadingExams: false,
          loadingSubjects: false,
          loadingWhatsApp: false,
          loadingEnrollment: false,
          loadingModules: false,
          loadingAcademic: false,
          loadingSeatCapacity: false,
          loadingTopDefaulters: false,
          loadingAdmissionFunnel: false,
          loadingRevenueOverview: false,
          loadingTodayAtGlance: false,
          loadingParentEngagement: false,
        });
      }
    };

    handleBranchChange = (e) => {
      const val = e.target.value;
      const branchId = val ? parseInt(val) : null;
      this.setState({ selectedBranchId: branchId }, () => {
        this.fetchDashboardData();
      });
    };

    getCalculatedValues = () => {
      const {
        totalsData,
        feeData,
        attendanceData,
        examResults,
        examSummary,
        subjectScores,
        overallAverage,
        whatsappData,
        enrollmentData,
        modulesData,
        loadingFee,
        loadingAttendance,
        loadingExams,
        loadingSubjects,
        loadingWhatsApp,
        loadingEnrollment,
        loadingModules,
        academicPerformance,
        seatCapacity,
        topDefaulters,
        admissionFunnel,
        revenueOverview,
        todayAtGlance,
        parentEngagement,
        loadingAcademic,
        loadingSeatCapacity,
        loadingTopDefaulters,
        loadingAdmissionFunnel,
        loadingRevenueOverview,
        loadingTodayAtGlance,
        loadingParentEngagement,
        expensesData,
        loadingExpenses,
        showSeatModal,
      } = this.state;

      const now = new Date();
      const monthName = now.toLocaleString("default", { month: "long" });
      const yr = now.getFullYear();
      const nextYr = String(yr + 1).slice(2);
      const fiscalYr = `${String(yr).slice(2)}-${nextYr}`;
      const todayDay = now.getDate();
      const todayMon = now.toLocaleString("default", { month: "short" });

      const fs = feeData?.summary || {};
      const collected = fs.paidAmount || 0;
      const outstanding = fs.pendingAmount || 0;
      const totalFeeTarget = fs.totalAmount || 0;
      const overdueFee = fs.overdueAmount || 0;
      const collPct = Math.round(fs.collectionPercentage || 0);
      const netFee = null;
      const terms = feeData?.termWiseData || [];
      const overdueStudents = terms.reduce((s, t) => s + (t.overdueStudents || 0), 0);
      const pendingStudentsTotal = terms.reduce((s, t) => s + (t.pendingStudents || 0), 0);
      const paidStudentsTotal = terms.reduce((s, t) => s + (t.paidStudents || 0), 0);

      const sAtt = attendanceData?.studentAttendance || {};
      const stAtt = attendanceData?.staffAttendance || {};
      const classW = attendanceData?.classWiseAttendance || [];
      const attPct = Math.round(sAtt.presentPercentage || 0);
      const presentSt = sAtt.present || 0;
      const absentSt = sAtt.absent || 0;
      const totalSt = sAtt.totalStudents || 0;
      const totalStff = stAtt.totalStaff || 0;
      const staffAbs = stAtt.absent || 0;
      const staffPres = (stAtt.teaching || 0) + (stAtt.nonTeaching || 0);
      const staffPct = Math.round(
        stAtt.presentPercentage ||
        (totalStff > 0 ? (staffPres / totalStff) * 100 : staffPres > 0 ? 100 : 0)
      );
      const classOnTimeFallback = classW.filter((c) => (c.percentage || 0) >= 70).length;
      const classSubFallback = classW.filter((c) => (c.percentage || 0) < 70 && c.total > 0).length;

      const totals = totalsData || {};
      const totalStudents = totals.totalStudents || modulesData?.students?.count || 0;
      const newAdmissions = totals.newAdmissions || modulesData?.admissions?.count || 0;
      const pendingLeads = totals.pendingLeads || modulesData?.leads?.count || 0;

      const passRate = Math.round(examSummary?.averagePassRate || overallAverage || 0);
      const topScorerName = examSummary?.topScorerName || "";
      const topScorerScore = Math.round(examSummary?.topScorerScore || 0);
      const examsMonth = examSummary?.examsThisMonth || 0;
      const atRisk = examResults.filter((e) => (e.passRate || 0) < 50).length;
      const toppers = examResults.filter((e) => (e.passRate || 0) >= 90).length;
      const weakSubject = subjectScores.length > 0
        ? [...subjectScores].sort((a, b) => (a.averageScore || 0) - (b.averageScore || 0))[0]
        : null;
      const gradeRows =
        academicPerformance?.gradePerformance?.length > 0
          ? academicPerformance.gradePerformance
            .slice(0, 6)
            .map((g) => ({
              g: g.gradeName,
              p: Math.round(g.passPercentage || 0),
              c: pctColor(Math.round(g.passPercentage || 0)),
            }))
          : [];
      const examTrendData =
        academicPerformance?.examTrend?.length > 0
          ? academicPerformance.examTrend
          : examResults.map((e) => ({
            examName: e.examName,
            avgScore: e.averageScore || 0,
            passRate: e.passRate || 0,
          }));

      const revDaily = revenueOverview?.dailyData || [];
      const chartBars = revDaily.length > 0
        ? revDaily.map((d, i, arr) => ({
          label: d.date,
          value: d.amount || 0,
          isHighlight: i === arr.length - 1,
        }))
        : (enrollmentData?.monthlyData || []).map((m, i, arr) => ({
          label: m.month || MONTH_SHORT[i % 12],
          value: m.count || 0,
          isHighlight: i === arr.length - 1,
        }));
      const chartMax = Math.max(...chartBars.map((b) => b.value), 1);

      const revCollected = revenueOverview?.totalCollected || collected;
      const revOutstanding = revenueOverview?.totalOutstanding || outstanding;
      const revNet = revenueOverview?.netRevenue || netFee;
      const revReceipts = revenueOverview?.receiptCount || paidStudentsTotal;
      const revCategories = revenueOverview?.categoryBreakdown || terms.slice(0, 4).map((t) => ({
        category: t.termName || `Term ${t.termNo}`,
        amount: t.totalCollected || 0,
        percentage: t.collectionPercentage || 0,
      }));

      const af = admissionFunnel;
      const leadsTotal = af?.totalLeads || pendingLeads || 0;
      const inquiries = af?.totalInquiries || 0;
      const visited = af?.totalVisited || 0;
      const applied = af?.totalApplied || 0;
      const enrolled = af?.totalEnrolled || newAdmissions;
      const convRate = af?.overallConversionRate?.toFixed(1) || (leadsTotal > 0 ? ((enrolled / leadsTotal) * 100).toFixed(1) : "0");
      const avgDaysEnroll = af?.avgDaysToEnroll || null;

      const tag = todayAtGlance;
      const classOnTime = tag?.classStats?.classesOnTime || classOnTimeFallback;
      const classSub = tag?.classStats?.classesSubstituted || classSubFallback;
      const classTotal = tag?.classStats?.totalClasses || classW.length;
      const classPct = tag?.classStats?.onTimePercentage || (classTotal > 0 ? Math.round((classOnTime / classTotal) * 100) : 0);
      const todayFeeAmt = tag?.feeStats?.amountCollected || 0;
      const todayReceipts = tag?.feeStats?.receiptCount || 0;
      const openTasksCount = tag?.openTasksCount ?? (modulesData?.tasks?.count || 0);
      const todayExamsCount = tag?.todayExamsCount || 0;
      const upcomingExamsCount = tag?.upcomingExamsCount || 0;
      const todayExamNames = tag?.todayExamNames || [];

      const currentMonthNum = now.getMonth();
      const currentYearNum = now.getFullYear();
      let totalExpensesThisMonth = 0;
      let pendingApprovalsCount = 0;
      (expensesData || []).forEach((e) => {
        if (e.payment_status === "Pending") {
          pendingApprovalsCount++;
        }
        if (e.expense_date) {
          const d = new Date(e.expense_date);
          if (d.getMonth() === currentMonthNum && d.getFullYear() === currentYearNum) {
            totalExpensesThisMonth += e.final_amount || e.amount || 0;
          }
        }
      });
      const recentExpenses = (expensesData || []).slice(0, 4);

      return {
        totalsData,
        feeData,
        attendanceData,
        examResults,
        examSummary,
        subjectScores,
        overallAverage,
        whatsappData,
        enrollmentData,
        modulesData,
        loadingFee,
        loadingAttendance,
        loadingExams,
        loadingSubjects,
        loadingWhatsApp,
        loadingEnrollment,
        loadingModules,
        academicPerformance,
        seatCapacity,
        topDefaulters,
        admissionFunnel,
        revenueOverview,
        todayAtGlance,
        parentEngagement,
        loadingAcademic,
        loadingSeatCapacity,
        loadingTopDefaulters,
        loadingAdmissionFunnel,
        loadingRevenueOverview,
        loadingTodayAtGlance,
        loadingParentEngagement,
        expensesData,
        loadingExpenses,
        showSeatModal,
        now,
        monthName,
        yr,
        nextYr,
        fiscalYr,
        todayDay,
        todayMon,
        collected,
        outstanding,
        totalFeeTarget,
        overdueFee,
        collPct,
        netFee,
        terms,
        overdueStudents,
        pendingStudentsTotal,
        paidStudentsTotal,
        sAtt,
        stAtt,
        classW,
        attPct,
        presentSt,
        absentSt,
        totalSt,
        totalStff,
        staffAbs,
        staffPres,
        staffPct,
        passRate,
        topScorerName,
        topScorerScore,
        examsMonth,
        atRisk,
        toppers,
        weakSubject,
        gradeRows,
        examTrendData,
        chartBars,
        chartMax,
        revCollected,
        revOutstanding,
        revNet,
        revReceipts,
        revCategories,
        leadsTotal,
        inquiries,
        visited,
        applied,
        enrolled,
        convRate,
        avgDaysEnroll,
        todayFeeAmt,
        todayReceipts,
        todayExamsCount,
        upcomingExamsCount,
        todayExamNames,
        totalExpensesThisMonth,
        pendingApprovalsCount,
        recentExpenses,
      };
    };

    renderTodayAtGlance = (vals) => {
      return (
        <div style={{ ...card, padding: 20, background: "linear-gradient(135deg, rgba(224, 242, 254, 0.25) 0%, rgba(255, 255, 255, 0.85) 100%)" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 14,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#6b7280"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="5" />
                <line x1="12" y1="1" x2="12" y2="3" />
                <line x1="12" y1="21" x2="12" y2="23" />
                <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
                <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
                <line x1="1" y1="12" x2="3" y2="12" />
                <line x1="21" y1="12" x2="23" y2="12" />
                <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
                <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
              </svg>
              <span style={{ fontSize: 13, fontWeight: 700 }}>
                Today at a glance
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
              <span style={{ fontSize: 11, color: "#6b7280" }}>
                {vals.todayDay} {vals.todayMon}
              </span>
              <span
                style={{
                  fontSize: 10,
                  background: "#dcfce7",
                  color: "#059669",
                  borderRadius: 10,
                  padding: "1px 7px",
                  fontWeight: 600,
                }}
              >
                Live
              </span>
            </div>
          </div>

          {[
            {
              bg: "#fce7f3",
              ic: "#db2777",
              svg: (
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="2" y="7" width="20" height="14" rx="2" />
                  <path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16" />
                </svg>
              ),
              lbl: "Staff present",
              sub: vals.loadingAttendance
                ? null
                : (vals.totalStff > 0 || vals.staffPres > 0)
                  ? `${vals.staffPres}${vals.totalStff > 0 ? `/${vals.totalStff}` : ""} . ${vals.staffAbs} absent`
                  : "No data",
              val: vals.loadingAttendance
                ? null
                : (vals.staffPct > 0 || vals.staffPres > 0 || vals.totalStff > 0)
                  ? `${vals.staffPct}%`
                  : "--",
              delta: vals.staffAbs > 0 ? `${vals.staffAbs} absent` : null,
              dc: vals.staffAbs > 0 ? "#d97706" : "#059669",
              loading: vals.loadingAttendance,
            },
            {
              bg: "#fef3c7",
              ic: "#d97706",
              svg: (
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                </svg>
              ),
              lbl: "Fee collected today",
              sub: vals.loadingFee && vals.loadingTodayAtGlance
                ? null
                : vals.todayFeeAmt > 0
                  ? `${vals.todayReceipts} receipts . today`
                  : `${vals.paidStudentsTotal} total receipts`,
              val: vals.loadingFee && vals.loadingTodayAtGlance
                ? null
                : vals.todayFeeAmt > 0
                  ? fmtINR(vals.todayFeeAmt)
                  : fmtINR(vals.collected),
              delta: vals.todayFeeAmt > 0 ? "↑ Today" : vals.collPct > 0 ? `${vals.collPct}% collected` : null,
              dc: "#059669",
              loading: vals.loadingFee,
            },
            {
              bg: "#e0f2fe",
              ic: "#0284c7",
              svg: (
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                </svg>
              ),
              lbl: "Scheduled Exams",
              sub: vals.loadingTodayAtGlance
                ? null
                : vals.todayExamsCount > 0
                  ? `${vals.todayExamsCount} exam${vals.todayExamsCount > 1 ? "s" : ""} scheduled today${
                      vals.todayExamNames && vals.todayExamNames.length > 0 
                        ? `: ${vals.todayExamNames.join(", ")}` 
                        : ""
                    }`
                  : "No exams today",
              val: vals.loadingTodayAtGlance
                ? null
                : String(vals.todayExamsCount),
              delta: vals.upcomingExamsCount > 0 ? `${vals.upcomingExamsCount} upcoming` : null,
              dc: vals.todayExamsCount > 0 ? "#0284c7" : "#6b7280",
              loading: vals.loadingTodayAtGlance,
            },
          ].map((item, i, arr) => (
            <div
              key={i}
              className="dsh-row"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "9px 4px",
                borderBottom: i < arr.length - 1 ? "1px solid #f3f4f6" : "none",
              }}
            >
              <div
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: "50%",
                  background: item.bg,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: item.ic,
                  flexShrink: 0,
                }}
              >
                {item.svg}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 500, color: "#111827" }}>
                  {item.lbl}
                </div>
                {item.loading ? (
                  <Sk h={10} w="70%" />
                ) : (
                  <div style={{ fontSize: 10, color: "#9ca3af", marginTop: 1 }}>
                    {item.sub}
                  </div>
                )}
              </div>
              <div style={{ textAlign: "right", flexShrink: 0 }}>
                {item.loading ? (
                  <Sk h={14} w={40} />
                ) : (
                  <>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>
                      {item.val}
                    </div>
                    {item.delta && (
                      <div style={{ fontSize: 10, fontWeight: 600, color: item.dc }}>
                        {item.delta}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      );
    };

    renderAttendanceSummary = (vals) => {
      return (
        <div style={{ ...card, padding: 20, background: "linear-gradient(135deg, rgba(209, 250, 229, 0.25) 0%, rgba(255, 255, 255, 0.85) 100%)" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 16,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#6b7280"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 00-3-3.87" />
                <path d="M16 3.13a4 4 0 010 7.75" />
              </svg>
              <span style={{ fontSize: 15, fontWeight: 700 }}>
                Attendance Summary & Trends
              </span>
            </div>
            <OutlineBtn to="/attendance-regularization">Full report-&gt;</OutlineBtn>
          </div>

          {vals.loadingAttendance ? (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr",
                gap: 10,
                marginBottom: 16,
              }}
            >
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  style={{
                    background: "#f9fafb",
                    borderRadius: 8,
                    padding: "12px 14px",
                  }}
                >
                  <Sk h={14} w="60%" />
                  <div style={{ height: 8 }} />
                  <Sk h={20} />
                </div>
              ))}
            </div>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr",
                gap: 10,
                marginBottom: 16,
              }}
            >
              <div
                style={{
                  background: "#f9fafb",
                  borderRadius: 8,
                  padding: "12px 14px",
                }}
              >
                <div style={{ ...lbl, marginBottom: 6 }}>TOTAL REGISTERED</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: "#111827" }}>
                  {vals.totalSt.toLocaleString()}
                </div>
              </div>
              <div
                style={{
                  background: "#e8f5e9",
                  borderRadius: 8,
                  padding: "12px 14px",
                }}
              >
                <div style={{ ...lbl, marginBottom: 6, color: "#2e7d32" }}>PRESENT</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: "#2e7d32" }}>
                  {vals.presentSt.toLocaleString()}
                </div>
              </div>
              <div
                style={{
                  background: "#ffebee",
                  borderRadius: 8,
                  padding: "12px 14px",
                }}
              >
                <div style={{ ...lbl, marginBottom: 6, color: "#c62828" }}>ABSENT</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: "#c62828" }}>
                  {vals.absentSt.toLocaleString()}
                </div>
              </div>
            </div>
          )}

          {/* Class-wise present rates */}
          {!vals.loadingAttendance && vals.classW.length > 0 && (
            <div
              style={{
                borderTop: "1px solid #f3f4f6",
                marginTop: 14,
                paddingTop: 14,
              }}
            >
              <div style={{ ...lbl, marginBottom: 12 }}>
                CLASS-WISE ATTENDANCE RATE
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "10px 24px",
                }}
              >
                {vals.classW.slice(0, 10).map((c, i) => {
                  const pct = Math.round(c.percentage || (c.total > 0 ? (c.present / c.total) * 100 : 0));
                  const barColor = pct >= 90 ? "#10b981" : pct >= 75 ? "#3b82f6" : "#f59e0b";
                  return (
                    <div
                      key={i}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                      }}
                    >
                      <div
                        style={{
                          width: 80,
                          fontSize: 12,
                          color: "#374151",
                          fontWeight: 500,
                          textOverflow: "ellipsis",
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {c.className}
                      </div>
                      <div
                        style={{
                          flex: 1,
                          height: 8,
                          background: "#f3f4f6",
                          borderRadius: 4,
                          overflow: "hidden",
                        }}
                      >
                        <div
                          style={{
                            height: "100%",
                            width: `${pct}%`,
                            background: barColor,
                            borderRadius: 4,
                          }}
                        />
                      </div>
                      <div
                        style={{
                          width: 65,
                          fontSize: 11,
                          fontWeight: 600,
                          color: "#4b5563",
                          textAlign: "right",
                        }}
                      >
                        {c.present}/{c.total} ({pct}%)
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      );
    };

    renderRevenue = (vals) => {
      return (
        <div style={{ ...card, padding: 20, background: "linear-gradient(135deg, rgba(219, 234, 254, 0.25) 0%, rgba(255, 255, 255, 0.85) 100%)" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 16,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#6b7280"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="20" x2="18" y2="10" />
                <line x1="12" y1="20" x2="12" y2="4" />
                <line x1="6" y1="20" x2="6" y2="14" />
              </svg>
              <span style={{ fontSize: 15, fontWeight: 700 }}>
                Revenue -- {vals.monthName} {vals.yr}
              </span>
            </div>
            <OutlineBtn to="/Fee-Structure">Full report -&gt;</OutlineBtn>
          </div>

          {vals.loadingFee ? (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr",
                gap: 10,
                marginBottom: 16,
              }}
            >
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  style={{
                    background: "#f9fafb",
                    borderRadius: 8,
                    padding: "12px 14px",
                  }}
                >
                  <Sk h={16} w="60%" />
                  <div style={{ height: 8 }} />
                  <Sk h={22} />
                  <div style={{ height: 6 }} />
                  <Sk h={12} w="50%" />
                </div>
              ))}
            </div>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr",
                gap: 10,
                marginBottom: 16,
              }}
            >
              <div
                style={{
                  background: "#f9fafb",
                  borderRadius: 8,
                  padding: "12px 14px",
                }}
              >
                <div style={{ ...lbl, marginBottom: 6 }}>COLLECTED</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: "#059669" }}>
                  {fmtINRFull(vals.revCollected)}
                </div>
                <div style={{ fontSize: 11, color: "#6b7280", marginTop: 3 }}>
                  {vals.revReceipts} receipts
                </div>
              </div>
              <div
                style={{
                  background: "#fff5f5",
                  borderRadius: 8,
                  padding: "12px 14px",
                }}
              >
                <div style={{ ...lbl, marginBottom: 6 }}>OUTSTANDING</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: "#ef4444" }}>
                  {fmtINRFull(vals.revOutstanding)}
                </div>
                <div style={{ fontSize: 11, color: "#6b7280", marginTop: 3 }}>
                  {vals.pendingStudentsTotal} students
                </div>
              </div>
              <div
                style={{
                  background: "#f9fafb",
                  borderRadius: 8,
                  padding: "12px 14px",
                }}
              >
                <div style={{ ...lbl, marginBottom: 6 }}>
                  NET (AFTER EXPENSES)
                </div>
                <div style={{ fontSize: 22, fontWeight: 700, color: "#111827" }}>
                  {vals.revNet != null ? fmtINRFull(vals.revNet) : "--"}
                </div>
                <div style={{ marginTop: 5 }}>
                  {vals.revNet == null ? (
                    <span style={{ fontSize: 11, color: "#9ca3af" }}>
                      No expense data
                    </span>
                  ) : vals.collPct > 0 && (
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: "#059669",
                        background: "#dcfce7",
                        borderRadius: 20,
                        padding: "2px 8px",
                      }}
                    >
                      â†‘ {vals.collPct}% collected
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Enrollment trend chart */}
          {vals.loadingEnrollment && vals.loadingRevenueOverview ? (
            <Sk h={76} />
          ) : vals.chartBars.length > 0 ? (
            <>
              <svg
                width="100%"
                height={76}
                viewBox={`0 0 ${vals.chartBars.length * 20} 76`}
                preserveAspectRatio="none"
              >
                {vals.chartBars.map((b, i) => {
                  const bh = Math.max(3, (b.value / vals.chartMax) * 68);
                  return (
                    <rect
                      key={i}
                      x={i * 20}
                      y={76 - bh}
                      width={17}
                      height={bh}
                      rx={2}
                      fill={b.isHighlight ? "#3b82f6" : "#bfdbfe"}
                    />
                  );
                })}
              </svg>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginTop: 4,
                }}
              >
                <span style={{ fontSize: 10, color: "#9ca3af" }}>
                  {vals.chartBars[0]?.label}
                </span>
                <span
                  style={{
                    fontSize: 10,
                    color: "#3b82f6",
                    fontWeight: 600,
                  }}
                >
                  {vals.todayDay} {vals.todayMon} (today)
                </span>
                <span style={{ fontSize: 10, color: "#9ca3af" }}>
                  {vals.chartBars[vals.chartBars.length - 1]?.label}
                </span>
              </div>
            </>
          ) : null}

          {/* Term breakdown */}
          {!vals.loadingFee && vals.terms.length > 0 && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: `repeat(${Math.min(vals.terms.length, 4)},1fr)`,
                borderTop: "1px solid #f3f4f6",
                marginTop: 14,
                paddingTop: 14,
              }}
            >
              {vals.terms.slice(0, 4).map((t, i) => (
                <div
                  key={i}
                  style={{
                    paddingLeft: i > 0 ? 14 : 0,
                    borderLeft: i > 0 ? "1px solid #f3f4f6" : "none",
                  }}
                >
                  <div style={{ ...lbl, fontSize: 9, marginBottom: 4 }}>
                    {t.termName || `TERM ${t.termNo}`}
                  </div>
                  <div style={{ fontSize: 16, fontWeight: 700 }}>
                    {fmtINR(t.totalCollected)}
                  </div>
                  <div style={{ fontSize: 11, color: "#6b7280" }}>
                    {Math.round(t.collectionPercentage || 0)}% .{" "}
                    {(t.totalCollected || 0) > (t.totalExpected || 0) * 0.5 ? "â†‘" : "â†“"}
                    {Math.round(t.collectionPercentage || 0)}%
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    };

    renderExpenses = (vals) => {
      return (
        <div style={{ ...card, padding: 20, background: "linear-gradient(135deg, rgba(254, 226, 226, 0.25) 0%, rgba(255, 255, 255, 0.85) 100%)" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 16,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#6b7280"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="2" y="4" width="20" height="16" rx="2" />
                <line x1="12" y1="4" x2="12" y2="20" />
                <line x1="2" y1="12" x2="22" y2="12" />
              </svg>
              <span style={{ fontSize: 15, fontWeight: 700 }}>
                Expenses -- {vals.monthName} {vals.yr}
              </span>
            </div>
            <OutlineBtn to="/expense-management">Full manager -&gt;</OutlineBtn>
          </div>

          {vals.loadingExpenses ? (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 10,
                marginBottom: 16,
              }}
            >
              {[1, 2].map((i) => (
                <div
                  key={i}
                  style={{
                    background: "#f9fafb",
                    borderRadius: 8,
                    padding: "12px 14px",
                  }}
                >
                  <Sk h={14} w="60%" />
                  <div style={{ height: 8 }} />
                  <Sk h={20} />
                </div>
              ))}
            </div>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 10,
                marginBottom: 16,
              }}
            >
              <div
                style={{
                  background: "#f9fafb",
                  borderRadius: 8,
                  padding: "12px 14px",
                }}
              >
                <div style={{ ...lbl, marginBottom: 6 }}>TOTAL SPENT</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: "#ef4444" }}>
                  {fmtINRFull(vals.totalExpensesThisMonth)}
                </div>
                <div style={{ fontSize: 11, color: "#6b7280", marginTop: 3 }}>
                  This month
                </div>
              </div>
              <div
                style={{
                  background: vals.pendingApprovalsCount > 0 ? "#fff5f5" : "#f9fafb",
                  borderRadius: 8,
                  padding: "12px 14px",
                }}
              >
                <div style={{ ...lbl, marginBottom: 6 }}>PENDING APPROVAL</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: vals.pendingApprovalsCount > 0 ? "#ef4444" : "#6b7280" }}>
                  {vals.pendingApprovalsCount}
                </div>
                <div style={{ fontSize: 11, color: "#6b7280", marginTop: 3 }}>
                  Requires action
                </div>
              </div>
            </div>
          )}

          {/* Recent Expenses List */}
          {!vals.loadingExpenses && vals.recentExpenses.length > 0 && (
            <div
              style={{
                borderTop: "1px solid #f3f4f6",
                marginTop: 14,
                paddingTop: 14,
              }}
            >
              <div style={{ ...lbl, marginBottom: 12 }}>
                RECENT EXPENSES LOGGED
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                }}
              >
                {vals.recentExpenses.map((e, i) => (
                  <div
                    key={i}
                    className="dsh-row"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "8px 10px",
                      borderBottom: i < vals.recentExpenses.length - 1 ? "1px solid #f9fafb" : "none",
                    }}
                  >
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 600, color: "#111827" }}>
                        {e.head_name || "General"} . <span style={{ fontWeight: 400, color: "#6b7280" }}>{e.category_name}</span>
                      </div>
                      <div style={{ fontSize: 10, color: "#9ca3af", marginTop: 2 }}>
                        {e.vendor_name ? `${e.vendor_name} . ` : ""}{e.expense_date}
                      </div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>
                        {fmtINR(e.final_amount || e.amount || 0)}
                      </div>
                      {e.payment_status && (
                        <span
                          style={{
                            fontSize: 9,
                            fontWeight: 600,
                            padding: "1px 6px",
                            borderRadius: 10,
                            background: e.payment_status === "Paid" ? "#dcfce7" : "#fee2e2",
                            color: e.payment_status === "Paid" ? "#15803d" : "#b91c1c",
                            display: "inline-block",
                            marginTop: 2,
                          }}
                        >
                          {e.payment_status}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      );
    };

    renderAcademicPerformance = (vals) => {
      return (
        <div style={{ ...card, padding: 20, background: "linear-gradient(135deg, rgba(237, 233, 254, 0.25) 0%, rgba(255, 255, 255, 0.85) 100%)" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 16,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#6b7280"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
                <path d="M6 12v5c3 3 9 3 12 0v-5" />
              </svg>
              <span style={{ fontSize: 15, fontWeight: 700 }}>
                Academic Performance
              </span>
            </div>
            <OutlineBtn to="/ExamReport">Reports -&gt;</OutlineBtn>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 24,
            }}
          >
            {/* Pass % by grade -- Phase 2 data */}
            <div>
              <div style={{ ...lbl, marginBottom: 12 }}>
                PASS % BY GRADE
              </div>
              {vals.loadingAcademic ? (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 10,
                  }}
                >
                  {[1, 2, 3, 4, 5, 6].map((i) => (
                    <Sk key={i} h={16} />
                  ))}
                </div>
              ) : vals.gradeRows.length === 0 ? (
                <div style={{ padding: "16px 0", textAlign: "center", color: "#9ca3af", fontSize: 12 }}>
                  No exam data available
                </div>
              ) : (
                vals.gradeRows.map((row, i) => (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      marginBottom: 10,
                    }}
                  >
                    <div
                      style={{
                        width: 62,
                        fontSize: 12,
                        color: "#374151",
                        fontWeight: 500,
                        flexShrink: 0,
                      }}
                    >
                      {row.g}
                    </div>
                    <div
                      style={{
                        flex: 1,
                        height: 8,
                        background: "#f3f4f6",
                        borderRadius: 4,
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          height: "100%",
                          width: `${row.p}%`,
                          background: row.c,
                          borderRadius: 4,
                        }}
                      />
                    </div>
                    <div
                      style={{
                        width: 32,
                        fontSize: 12,
                        fontWeight: 700,
                        color: row.c,
                        textAlign: "right",
                      }}
                    >
                      {row.p}%
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Exam score trend + summary */}
            <div>
              <div style={{ ...lbl, marginBottom: 10 }}>
                SCHOOL AVG SCORE (LAST {vals.examTrendData.length} EXAMS)
              </div>
              {vals.loadingExams && vals.loadingAcademic ? (
                <Sk h={72} />
              ) : vals.examTrendData.length > 0 ? (
                <>
                  <svg
                    width="100%"
                    height={72}
                    viewBox={`0 0 ${vals.examTrendData.length * 30} 72`}
                    preserveAspectRatio="none"
                  >
                    {vals.examTrendData.map((e, i) => {
                      const bh = Math.max(
                        3,
                        ((e.avgScore || e.averageScore || 0) / 100) * 64,
                      );
                      return (
                        <rect
                          key={i}
                          x={i * 30}
                          y={72 - bh}
                          width={26}
                          height={bh}
                          rx={3}
                          fill={
                            i === vals.examTrendData.length - 1
                              ? "#3b82f6"
                              : "#bfdbfe"
                          }
                        />
                      );
                    })}
                  </svg>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginTop: 3,
                      marginBottom: 12,
                    }}
                  >
                    <span style={{ fontSize: 10, color: "#9ca3af" }}>
                      {vals.examTrendData[0]?.examDate
                        ? new Date(
                          vals.examTrendData[0].examDate,
                        ).toLocaleDateString("en-IN", {
                          month: "short",
                          year: "2-digit",
                        })
                        : "Oldest"}
                    </span>
                    <span
                      style={{
                        fontSize: 10,
                        color: "#3b82f6",
                        fontWeight: 600,
                      }}
                    >
                      Latest
                    </span>
                  </div>
                </>
              ) : (
                <div
                  style={{
                    height: 72,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "#f9fafb",
                    borderRadius: 6,
                  }}
                >
                  <span style={{ fontSize: 11, color: "#9ca3af" }}>
                    No exam data
                  </span>
                </div>
              )}

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 8,
                  marginTop: 4,
                }}
              >
                {vals.topScorerName && (
                  <div
                    style={{
                      background: "#f9fafb",
                      borderRadius: 8,
                      padding: "8px 10px",
                    }}
                  >
                    <div style={{ ...lbl, marginBottom: 3 }}>
                      TOP SCORER
                    </div>
                    <div
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: "#111827",
                      }}
                    >
                      {vals.topScorerName} . {vals.topScorerScore}%
                    </div>
                  </div>
                )}
                {vals.toppers > 0 && (
                  <div
                    style={{
                      background: "#f9fafb",
                      borderRadius: 8,
                      padding: "8px 10px",
                    }}
                  >
                    <div style={{ ...lbl, marginBottom: 3 }}>
                      TOPPERS (90%+)
                    </div>
                    <div
                      style={{
                        fontSize: 16,
                        fontWeight: 700,
                        color: "#111827",
                      }}
                    >
                      {vals.toppers}{" "}
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 400,
                          color: "#6b7280",
                        }}
                      >
                        exams
                      </span>
                    </div>
                  </div>
                )}
                {vals.atRisk > 0 && (
                  <div
                    style={{
                      background: "#fef2f2",
                      borderRadius: 8,
                      padding: "8px 10px",
                    }}
                  >
                    <div
                      style={{ ...lbl, color: "#dc2626", marginBottom: 3 }}
                    >
                      AT-RISK (&lt;50%)
                    </div>
                    <div
                      style={{
                        fontSize: 16,
                        fontWeight: 700,
                        color: "#dc2626",
                      }}
                    >
                      {vals.atRisk}{" "}
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 400,
                          color: "#6b7280",
                        }}
                      >
                        exams
                      </span>
                    </div>
                  </div>
                )}
                {vals.weakSubject && (
                  <div
                    style={{
                      background: "#fffbeb",
                      borderRadius: 8,
                      padding: "8px 10px",
                    }}
                  >
                    <div
                      style={{ ...lbl, color: "#d97706", marginBottom: 3 }}
                    >
                      WEAK SUBJECT
                    </div>
                    <div
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: "#111827",
                      }}
                    >
                      {vals.weakSubject.subjectName} .{" "}
                      <span style={{ color: "#d97706" }}>
                        {Math.round(vals.weakSubject.averageScore)}%
                      </span>
                    </div>
                  </div>
                )}
                {vals.overallAverage > 0 && (
                  <div
                    style={{
                      background: "#f0fdf4",
                      borderRadius: 8,
                      padding: "8px 10px",
                    }}
                  >
                    <div
                      style={{ ...lbl, color: "#059669", marginBottom: 3 }}
                    >
                      OVERALL AVG
                    </div>
                    <div
                      style={{
                        fontSize: 16,
                        fontWeight: 700,
                        color: "#059669",
                      }}
                    >
                      {Math.round(vals.overallAverage)}%
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    };

    renderAdmissionFunnel = (vals) => {
      return (
        <div style={{ ...card, padding: 20, background: "linear-gradient(135deg, rgba(254, 243, 199, 0.25) 0%, rgba(255, 255, 255, 0.85) 100%)" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 16,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#6b7280"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
              </svg>
              <span style={{ fontSize: 15, fontWeight: 700 }}>
                Admission Funnel -- AY {vals.fiscalYr}
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 12, color: "#6b7280" }}>
                {vals.enrolled} enrolled of {vals.totalStudents} total
              </span>
              <OutlineBtn to="/lead-management-home">Pipeline -&gt;</OutlineBtn>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "stretch",
              gap: 6,
              marginBottom: 14,
            }}
          >
            {[
              {
                val: vals.leadsTotal,
                lbl: "Leads",
                sub: `${vals.pendingLeads} pending`,
                bg: "#f9fafb",
                border: "#e5e7eb",
                vc: "#111827",
                sc: "#059669",
                tc: "#111827",
              },
              {
                val: vals.inquiries,
                lbl: "Inquiries",
                sub: "59% conv.",
                bg: "#f9fafb",
                border: "#e5e7eb",
                vc: "#111827",
                sc: "#6b7280",
                tc: "#111827",
              },
              {
                val: vals.visited,
                lbl: "Visited",
                sub: "campus visits",
                bg: "#f9fafb",
                border: "#e5e7eb",
                vc: "#111827",
                sc: "#6b7280",
                tc: "#111827",
              },
              {
                val: vals.applied,
                lbl: "Applied",
                sub: "applications",
                bg: "#dbeafe",
                border: "#bfdbfe",
                vc: "#1d4ed8",
                sc: "#6b7280",
                tc: "#1d4ed8",
              },
              {
                val: vals.enrolled,
                lbl: "Enrolled",
                sub: "confirmed",
                bg: "#2563eb",
                border: "#2563eb",
                vc: "#fff",
                sc: "rgba(255,255,255,.7)",
                tc: "#fff",
              },
            ].map((s, i, arr) => (
              <React.Fragment key={i}>
                <div
                  style={{
                    flex: 1,
                    background: s.bg,
                    border: `1.5px solid ${s.border}`,
                    borderRadius: 10,
                    padding: "12px 10px",
                    textAlign: "center",
                  }}
                >
                  <div
                    style={{
                      fontSize: 24,
                      fontWeight: 700,
                      color: s.vc,
                      lineHeight: 1.1,
                    }}
                  >
                    {(s.val || 0).toLocaleString()}
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: s.tc,
                      margin: "3px 0 2px",
                    }}
                  >
                    {s.lbl}
                  </div>
                  <div style={{ fontSize: 11, color: s.sc }}>{s.sub}</div>
                </div>
                {i < arr.length - 1 && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      color: "#d1d5db",
                      fontSize: 20,
                      flexShrink: 0,
                    }}
                  >

                  </div>
                )}
              </React.Fragment>
            ))}
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 20,
              borderTop: "1px solid #f3f4f6",
              paddingTop: 12,
              flexWrap: "wrap",
            }}
          >
            <span style={{ fontSize: 12, color: "#6b7280" }}>
              <span style={{ color: "#059669" }}>^</span> Overall conversion{" "}
              {vals.convRate}%
            </span>
            {vals.avgDaysEnroll != null && (
              <span style={{ fontSize: 12, color: "#6b7280" }}>
                Avg {vals.avgDaysEnroll} days to enroll
              </span>
            )}
            <span style={{ fontSize: 12, color: "#6b7280" }}>
              <svg
                width="11"
                height="11"
                style={{ verticalAlign: "middle", marginRight: 3 }}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              >
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
              {vals.avgDaysEnroll != null ? `Avg lead -> enrolled ${vals.avgDaysEnroll} days` : null}
            </span>
            <Link
              to="/lead-management-home"
              style={{
                fontSize: 12,
                color: "#3b82f6",
                textDecoration: "none",
                fontWeight: 500,
                marginLeft: "auto",
              }}
            >
              Source breakdown -&gt;
            </Link>
          </div>
        </div>
      );
    };

    renderQuickActions = (vals) => {
      return (
        <div style={{ ...card, padding: "14px 20px", background: "linear-gradient(135deg, rgba(238, 242, 255, 0.25) 0%, rgba(255, 255, 255, 0.85) 100%)" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              marginBottom: 12,
            }}
          >
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="#f59e0b"
              stroke="#f59e0b"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
            </svg>
            <span style={{ fontSize: 13, fontWeight: 600 }}>
              Quick Actions
            </span>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(8,1fr)",
              gap: 8,
            }}
          >
            {[
              {
                lbl: "New Admission",
                to: "/admission-forms",
                cat: "admissions",
                ico: (
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#374151"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <line x1="19" y1="8" x2="19" y2="14" />
                    <line x1="22" y1="11" x2="16" y2="11" />
                  </svg>
                ),
              },
              {
                lbl: "Collect Fee",
                to: "/Fee-Structure",
                cat: "revenue",
                ico: (
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#374151"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="2" y="4" width="20" height="16" rx="2" />
                    <line x1="12" y1="4" x2="12" y2="20" />
                    <line x1="2" y1="12" x2="22" y2="12" />
                  </svg>
                ),
              },
              {
                lbl: "Add Expense",
                to: "/expense-management/add",
                cat: "expenses",
                ico: (
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#374151"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                ),
              },
              {
                lbl: "Time Table",
                to: "/lecturer-timeTable",
                cat: "academic",
                ico: (
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#374151"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                  </svg>
                ),
              },
              {
                lbl: "Attendance",
                to: "/student/attendance",
                cat: "attendance",
                ico: (
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#374151"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
                    <polyline points="22 4 12 14.01 9 11.01" />
                  </svg>
                ),
              },
              {
                lbl: "Exam Results",
                to: "/ExamReport",
                cat: "exams",
                ico: (
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#374151"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                    <polyline points="10 9 9 9 8 9" />
                  </svg>
                ),
              },
              {
                lbl: "Doubt Desk",
                to: "/student-doubts",
                cat: "doubts",
                mod: ["Student_Doubts", "Doubts"],
                ico: (
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#374151"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
                  </svg>
                ),
              },
              {
                lbl: "Student List",
                to: "/student-admission-list",
                cat: "students",
                ico: (
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#374151"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M23 21v-2a4 4 0 00-3-3.87" />
                    <path d="M16 3.13a4 4 0 010 7.75" />
                  </svg>
                ),
              },
            ]
              // Hide any quick-action whose module the role can't view, so a tile
              // never dead-ends at the Access Denied page. Tiles without `mod` are
              // ungated. canView() returns true for admins (userlevel 1/2).
              .filter((qa) => !qa.mod || canView(qa.mod))
              .map((qa, i) => (
              <Link key={i} to={qa.to} style={{ textDecoration: "none" }}>
                <div
                  className={`dsh-qa dsh-qa-${qa.cat}`}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 6,
                    padding: "10px 4px",
                    border: "1px solid #e5e7eb",
                    borderRadius: 10,
                    background: "#fff",
                    cursor: "pointer",
                  }}
                >
                  {qa.ico}
                  <span
                    style={{
                      fontSize: 10,
                      color: "#374151",
                      textAlign: "center",
                      fontWeight: 500,
                      lineHeight: 1.3,
                    }}
                  >
                    {qa.lbl}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      );
    };

    renderAlertFeedDrawer = () => {
      const { isDrawerOpen } = this.state;

      // Modern timestamped mock alerts for the drawer feed
      const drawerAlerts = [
        {
          id: 1,
          title: "Student Registration Check-in",
          desc: "Amit Sharma (Class X-A) checked in at the main gate.",
          time: "Just now",
          type: "checkin",
          icon: (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
          ),
          bg: "rgba(52, 211, 153, 0.15)",
          pulseColor: "#10b981",
        },
        {
          id: 2,
          title: "Fee Clearance Log",
          desc: "Receipt of ₹12,500 generated for Priya Patel (Class XII-B).",
          time: "12 mins ago",
          type: "fee",
          icon: (
            <span style={{ fontSize: 13, fontWeight: 700, color: "#047857" }}>₹</span>
          ),
          bg: "rgba(16, 185, 129, 0.15)",
          pulseColor: "#059669",
        },
        {
          id: 3,
          title: "Exam Paper Assigned",
          desc: "'Physics Grand Test - II' assigned to Class XII-A by Dr. S. Rao.",
          time: "1 hour ago",
          type: "exam",
          icon: (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
            </svg>
          ),
          bg: "rgba(245, 158, 11, 0.15)",
          pulseColor: "#f59e0b",
        },
        {
          id: 4,
          title: "Staff Attendance Alert",
          desc: "3 teaching staff marked absent. Substitutions recommended.",
          time: "2 hours ago",
          type: "staff",
          icon: (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
            </svg>
          ),
          bg: "rgba(239, 68, 68, 0.15)",
          pulseColor: "#ef4444",
        },
        {
          id: 5,
          title: "Doubt Desk Query",
          desc: "New Chemistry doubt uploaded by Rohan Verma (Class XI-C).",
          time: "3 hours ago",
          type: "doubt",
          icon: (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
          ),
          bg: "rgba(99, 102, 241, 0.15)",
          pulseColor: "#6366f1",
        }
      ];

      return (
        <>
          {/* Backdrop overlay */}
          <div
            onClick={() => this.setState({ isDrawerOpen: false })}
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              background: "rgba(17, 24, 39, 0.2)",
              backdropFilter: "blur(6px)",
              WebkitBackdropFilter: "blur(6px)",
              zIndex: 9998,
              opacity: isDrawerOpen ? 1 : 0,
              pointerEvents: isDrawerOpen ? "auto" : "none",
              transition: "opacity 0.4s cubic-bezier(0.16, 1, 0.3, 1)",
            }}
          />

          {/* Sliding drawer panel */}
          <div
            style={{
              position: "fixed",
              top: 0,
              right: 0,
              width: "min(400px, 90vw)",
              height: "100vh",
              background: "rgba(255, 255, 255, 0.75)",
              backdropFilter: "blur(25px)",
              WebkitBackdropFilter: "blur(25px)",
              borderLeft: "1px solid rgba(255, 255, 255, 0.5)",
              boxShadow: "-10px 0 40px -10px rgba(0, 0, 0, 0.15)",
              zIndex: 9999,
              display: "flex",
              flexDirection: "column",
              transform: isDrawerOpen ? "translateX(0)" : "translateX(100%)",
              transition: "transform 0.4s cubic-bezier(0.16, 1, 0.3, 1)",
            }}
          >
            {/* Drawer Header */}
            {/* <div
              style={{
                padding: "20px 24px",
                borderBottom: "1px solid rgba(0, 0, 0, 0.06)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div>
                <h3 style={{ fontSize: 16, fontWeight: 800, color: "#111827", margin: 0 }}>
                  Operations Log
                </h3>
                <p style={{ fontSize: 11, color: "#6b7280", margin: "4px 0 0 0" }}>
                  Live console feed alerts
                </p>
              </div>
              <button
                onClick={() => this.setState({ isDrawerOpen: false })}
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  border: "none",
                  background: "rgba(0, 0, 0, 0.05)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  color: "#4b5563",
                  fontSize: 16,
                  fontWeight: 600,
                  outline: "none",
                  transition: "background 0.2s",
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = "rgba(0, 0, 0, 0.1)"}
                onMouseLeave={(e) => e.currentTarget.style.background = "rgba(0, 0, 0, 0.05)"}
              >
                ×
              </button>
            </div> */}

            {/* Drawer Content */}
            <div
              style={{
                flex: 1,
                overflowY: "auto",
                padding: "20px 24px",
                display: "flex",
                flexDirection: "column",
                gap: 14,
              }}
            >
              {drawerAlerts.map((alert) => (
                <div
                  key={alert.id}
                  style={{
                    display: "flex",
                    gap: 12,
                    padding: 14,
                    borderRadius: 12,
                    background: "rgba(255, 255, 255, 0.6)",
                    border: "1px solid rgba(255, 255, 255, 0.8)",
                    boxShadow: "0 4px 14px -4px rgba(0,0,0,0.03)",
                    position: "relative",
                    transition: "transform 0.2s, box-shadow 0.2s",
                    cursor: "pointer",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-1px)";
                    e.currentTarget.style.boxShadow = "0 6px 18px -4px rgba(0,0,0,0.06)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "none";
                    e.currentTarget.style.boxShadow = "0 4px 14px -4px rgba(0,0,0,0.03)";
                  }}
                >
                  {/* Active Indicator Pulse */}
                  <span
                    style={{
                      position: "absolute",
                      top: 14,
                      right: 14,
                      width: 6,
                      height: 6,
                      background: alert.pulseColor,
                      borderRadius: "50%",
                      boxShadow: `0 0 0 2px rgba(${alert.id === 4 ? "239, 68, 68" : "16, 185, 129"}, 0.2)`,
                      animation: "dsh-pulse 1.6s infinite",
                    }}
                  />

                  {/* Icon Wrapper */}
                  <div
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 8,
                      background: alert.bg,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    {alert.icon}
                  </div>

                  {/* Text Content */}
                  <div style={{ flex: 1, paddingRight: 10 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                      <h4 style={{ fontSize: 13, fontWeight: 700, color: "#111827", margin: 0 }}>
                        {alert.title}
                      </h4>
                    </div>
                    <p style={{ fontSize: 11, color: "#4b5563", margin: "4px 0 6px 0", lineHeight: 1.4 }}>
                      {alert.desc}
                    </p>
                    <span style={{ fontSize: 10, color: "#9ca3af", fontWeight: 500 }}>
                      {alert.time}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      );
    };

    render() {
      // is_view === 0 → block the whole dashboard. Above getCalculatedValues() and every
      // state dereference so no KPI/widget data is read or rendered; componentDidMount
      // already skipped the fetches. (Navigation OUT of the dashboard is gated separately
      // by is_edit via handleNavGuardCapture.)
      if (!canView(DASHBOARD_MODULE)) {
        return <AccessDeniedCard message="You do not have permission to view the Dashboard." />;
      }

      const vals = this.getCalculatedValues();
      const {
        totalsData,
        feeData,
        attendanceData,
        examResults,
        examSummary,
        subjectScores,
        overallAverage,
        whatsappData,
        enrollmentData,
        modulesData,
        loadingFee,
        loadingAttendance,
        loadingExams,
        loadingSubjects,
        loadingWhatsApp,
        loadingEnrollment,
        loadingModules,
        // Phase 2
        academicPerformance,
        seatCapacity,
        topDefaulters,
        admissionFunnel,
        revenueOverview,
        todayAtGlance,
        parentEngagement,
        loadingAcademic,
        loadingSeatCapacity,
        loadingTopDefaulters,
        loadingAdmissionFunnel,
        loadingRevenueOverview,
        loadingTodayAtGlance,
        loadingParentEngagement,
        showSeatModal,
      } = this.state;

      // -- Date helpers --------------------------------------------------------
      const now = new Date();
      const monthName = now.toLocaleString("default", { month: "long" });
      const yr = now.getFullYear();
      const nextYr = String(yr + 1).slice(2);
      const todayDay = now.getDate();
      const todayMon = MONTH_SHORT[now.getMonth()];
      const fiscalYr =
        now.getMonth() >= 3
          ? `${yr}-${nextYr}`
          : `${yr - 1}-${String(yr).slice(2)}`;

      // -- Fee data -------------------------------------------------------------
      const fs = feeData?.summary || {};
      const collected = fs.paidAmount || 0;
      const outstanding = fs.pendingAmount || 0;
      const totalFeeTarget = fs.totalAmount || 0;
      const overdueFee = fs.overdueAmount || 0;
      const collPct = Math.round(fs.collectionPercentage || 0);
      const netFee = null; // only show when real Phase-2 data is available
      const terms = feeData?.termWiseData || [];
      const overdueStudents = terms.reduce(
        (s, t) => s + (t.overdueStudents || 0),
        0,
      );
      const pendingStudentsTotal = terms.reduce(
        (s, t) => s + (t.pendingStudents || 0),
        0,
      );
      const paidStudentsTotal = terms.reduce(
        (s, t) => s + (t.paidStudents || 0),
        0,
      );

      // -- Attendance data ------------------------------------------------------
      const sAtt = attendanceData?.studentAttendance || {};
      const stAtt = attendanceData?.staffAttendance || {};
      const classW = attendanceData?.classWiseAttendance || [];
      const attPct = Math.round(sAtt.presentPercentage || 0);
      const presentSt = sAtt.present || 0;
      const absentSt = sAtt.absent || 0;
      const totalSt = sAtt.totalStudents || 0;
      const totalStff = stAtt.totalStaff || 0;
      const staffAbs = stAtt.absent || 0;
      const staffPres = (stAtt.teaching || 0) + (stAtt.nonTeaching || 0);
      const staffPct = Math.round(
        stAtt.presentPercentage ||
        (totalStff > 0 ? (staffPres / totalStff) * 100 : staffPres > 0 ? 100 : 0)
      );
      const classOnTimeFallback = classW.filter(
        (c) => (c.percentage || 0) >= 70,
      ).length;
      const classSubFallback = classW.filter(
        (c) => (c.percentage || 0) < 70 && c.total > 0,
      ).length;

      // -- Dashboard totals -----------------------------------------------------
      const totals = totalsData || {};
      const totalStudents =
        totals.totalStudents || modulesData?.students?.count || 0;
      const newAdmissions =
        totals.newAdmissions || modulesData?.admissions?.count || 0;
      const pendingLeads =
        totals.pendingLeads || modulesData?.leads?.count || 0;

      // -- Exam data ------------------------------------------------------------
      const passRate = Math.round(
        examSummary?.averagePassRate || overallAverage || 0,
      );
      const topScorer = examSummary?.topScorerName || "";
      const topScore = Math.round(examSummary?.topScorerScore || 0);
      const examsMonth = examSummary?.examsThisMonth || 0;
      const atRisk = examResults.filter((e) => (e.passRate || 0) < 50).length;
      const toppers = examResults.filter((e) => (e.passRate || 0) >= 90).length;
      const weakSubject =
        subjectScores.length > 0
          ? subjectScores.reduce(
            (w, s) => (s.averageScore < w.averageScore ? s : w),
            subjectScores[0],
          )
          : null;

      // -- WhatsApp -------------------------------------------------------------
      const ws = whatsappData?.messageStats || {};

      // -- Revenue chart: prefer Phase 2 daily data, fallback to enrollment -----
      const revDaily = revenueOverview?.dailyData || [];
      const chartBars =
        revDaily.length > 0
          ? revDaily.map((d, i, arr) => ({
            label: d.date,
            value: d.amount || 0,
            isHighlight: i === arr.length - 1,
          }))
          : (enrollmentData?.monthlyData || []).map((m, i, arr) => ({
            label: m.month || MONTH_SHORT[i % 12],
            value: m.count || 0,
            isHighlight: i === arr.length - 1,
          }));
      const chartMax = Math.max(...chartBars.map((b) => b.value), 1);

      // -- Revenue metrics: prefer Phase 2 --------------------------------------
      const revCollected = revenueOverview?.totalCollected || collected;
      const revOutstanding = revenueOverview?.totalOutstanding || outstanding;
      const revNet = revenueOverview?.netRevenue || netFee;
      const revReceipts = revenueOverview?.receiptCount || paidStudentsTotal;
      const revCategories =
        revenueOverview?.categoryBreakdown ||
        terms
          .slice(0, 4)
          .map((t) => ({
            category: t.termName || `Term ${t.termNo}`,
            amount: t.totalCollected || 0,
            percentage: t.collectionPercentage || 0,
          }));

      // -- Admission funnel: prefer Phase 2 -------------------------------------
      const af = admissionFunnel;
      const leadsTotal = af?.totalLeads || pendingLeads || 0;
      const inquiries = af?.totalInquiries || 0;
      const visited = af?.totalVisited || 0;
      const applied = af?.totalApplied || 0;
      const enrolled = af?.totalEnrolled || newAdmissions;
      const convRate =
        af?.overallConversionRate?.toFixed(1) ||
        (leadsTotal > 0 ? ((enrolled / leadsTotal) * 100).toFixed(1) : "0");
      const avgDaysEnroll = af?.avgDaysToEnroll || null;

      // ------ Today at a glance: prefer Phase 2 --------------------------------------------------------
      const tag = todayAtGlance;
      const classOnTime = tag?.classStats?.classesOnTime || classOnTimeFallback;
      const classSub = tag?.classStats?.classesSubstituted || classSubFallback;
      const classTotal = tag?.classStats?.totalClasses || classW.length;
      const classPct =
        tag?.classStats?.onTimePercentage ||
        (classTotal > 0 ? Math.round((classOnTime / classTotal) * 100) : 0);
      const todayFeeAmt = tag?.feeStats?.amountCollected || 0;
      const todayReceipts = tag?.feeStats?.receiptCount || 0;
      const openTasksCount =
        tag?.openTasksCount ?? (modulesData?.tasks?.count || 0);

      // -- Dynamic alerts -------------------------------------------------------
      const tdAmt = topDefaulters?.totalOverdueAmount || overdueFee;
      const tdCnt = topDefaulters?.totalDefaulters || overdueStudents;
      const alerts = [];
      if (tdCnt > 0)
        alerts.push({
          icon: "₹",
          iconBg: "#fee2e2",
          iconColor: "#dc2626",
          bg: "#fff5f5",
          title: `${tdCnt} fees overdue >30 days`,
          sub: `${fmtINR(tdAmt)} . escalate`,
        });
      if (pendingLeads > 5)
        alerts.push({
          icon: "ðŸŒ¡",
          iconBg: "#fef3c7",
          iconColor: "#d97706",
          bg: "#fffdf0",
          title: `${pendingLeads} leads going cold`,
          sub: "No follow-up >7 days",
        });
      if (staffAbs > 0)
        alerts.push({
          icon: "ðŸ‘¤",
          iconBg: "#fed7aa",
          iconColor: "#c2410c",
          bg: "#fff8f3",
          title: `${staffAbs} staff absent . no leave`,
          sub: "Substitutes pending",
        });
      if (openTasksCount > 0)
        alerts.push({
          icon: "ðŸ’¬",
          iconBg: "#ede9fe",
          iconColor: "#7c3aed",
          bg: "#faf5ff",
          title: `${openTasksCount} open tasks`,
          sub: "Action required",
        });

      // -- Seat capacity: only real getSeatCapacity data; no attendance fallback
      //    (attendance present/total is NOT seat capacity -- show empty instead).
      const seatRows =
        seatCapacity?.grades?.length > 0
          ? seatCapacity.grades
            .slice(0, 7)
            .map((g) => ({
              label: g.gradeName,
              f: g.enrolledCount,
              t: g.totalCapacity,
              color: pctColor(Math.round(g.utilizationPercentage || 0)),
            }))
          : [];

      // -- Academic performance: prefer Phase 2 by grade -------------------------
      const gradeRows =
        academicPerformance?.gradePerformance?.length > 0
          ? academicPerformance.gradePerformance
            .slice(0, 6)
            .map((g) => ({
              g: g.gradeName,
              p: Math.round(g.passPercentage || 0),
              c: pctColor(Math.round(g.passPercentage || 0)),
            }))
          : [];

      // -- Parent engagement: prefer Phase 2 ------------------------------------
      const pe = parentEngagement;
      const msgRead = pe?.totalMessagesRead || ws.read || 0;
      const msgReadRate = pe?.readRate || ws.readRate || 0;
      const msgReplied = pe?.totalReplied || ws.replied || 0;
      const openComp = pe?.openComplaints ?? openTasksCount;

      const broadcasts =
        pe?.topBroadcasts?.length > 0
          ? pe.topBroadcasts
            .slice(0, 3)
            .map((b) => ({
              name: b.name,
              count: b.sentCount,
              readPct: b.readRate,
            }))
          : (whatsappData?.broadcasts || [])
            .slice(0, 3)
            .map((b) => ({ name: b.name, count: b.count, readPct: null }));

      // -- Top defaulters: prefer Phase 2 ---------------------------------------
      const defaulterList = topDefaulters?.defaulters || [];

      // -- Exam trend: prefer Phase 2 academicPerformance.examTrend ------------
      const examTrendData =
        academicPerformance?.examTrend?.length > 0
          ? academicPerformance.examTrend
          : examResults.map((e) => ({
            examName: e.examName,
            avgScore: e.averageScore || 0,
            passRate: e.passRate || 0,
          }));
      const weakGrade =
        academicPerformance?.weakestGrade ||
        (weakSubject ? weakSubject.subjectName : null);
      const topGrade = academicPerformance?.topGrade || topScorer;

      const page = {
        background: "#eef0f4",
        minHeight: "100vh",
        padding: 20,
        fontFamily:
          '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif',
        color: "#111827",
        fontSize: 13,
        position: "relative",
      };

      const Chip = ({ text, down }) => (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            fontSize: 11,
            fontWeight: 600,
            color: down ? "#dc2626" : "#059669",
            background: down ? "#fee2e2" : "#dcfce7",
            borderRadius: 20,
            padding: "2px 8px",
            marginRight: 6,
          }}
        >
          {text}
        </span>
      );

      const ProgressBar = ({ pct, color }) => (
        <div
          style={{
            height: 6,
            background: "#e2e8f0",
            borderRadius: 99,
            marginTop: 14,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              height: "100%",
              width: `${Math.min(100, pct || 0)}%`,
              background: color,
              borderRadius: 99,
            }}
          />
        </div>
      );

      const IconCircle = ({ bg, color, size = 34, children }) => (
        <div
          style={{
            width: size,
            height: size,
            borderRadius: "50%",
            background: bg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color,
            flexShrink: 0,
          }}
        >
          {children}
        </div>
      );

      return (
        <div style={page} onClickCapture={this.handleNavGuardCapture}>
          <style>{`
            @keyframes dsh-pulse { 0%,100%{opacity:1} 50%{opacity:.45} }
            @keyframes dsh-shimmer { 0%{background-position:100% 50%} 100%{background-position:0 50%} }
            @keyframes dsh-slide-in { from { transform: translateX(100%); } to { transform: translateX(0); } }
            @keyframes dsh-bell-ring { 0%, 100% { transform: rotate(0); } 20%, 60% { transform: rotate(12deg); } 40%, 80% { transform: rotate(-12deg); } }
            
            .dsh-kpi { text-decoration:none; color:inherit; display:block; z-index: 1; }
            .dsh-kpi>div { transition:box-shadow 0.3s cubic-bezier(0.16, 1, 0.3, 1), transform 0.3s cubic-bezier(0.16, 1, 0.3, 1), border-color 0.3s ease; }
            .dsh-kpi:hover>div { transform: translateY(-4px); }
            
            /* Glow Shadows on Hover */
            .dsh-kpi-revenue:hover>div { box-shadow: 0 20px 38px -10px rgba(16, 185, 129, 0.18), 0 4px 6px -2px rgba(16, 185, 129, 0.05)!important; border-color: rgba(16, 185, 129, 0.35)!important; }
            .dsh-kpi-admissions:hover>div { box-shadow: 0 20px 38px -10px rgba(37, 99, 235, 0.18), 0 4px 6px -2px rgba(37, 99, 235, 0.05)!important; border-color: rgba(37, 99, 235, 0.35)!important; }
            .dsh-kpi-attendance:hover>div { box-shadow: 0 20px 38px -10px rgba(2, 132, 199, 0.18), 0 4px 6px -2px rgba(2, 132, 199, 0.05)!important; border-color: rgba(2, 132, 199, 0.35)!important; }
            .dsh-kpi-exams:hover>div { box-shadow: 0 20px 38px -10px rgba(217, 119, 6, 0.18), 0 4px 6px -2px rgba(217, 119, 6, 0.05)!important; border-color: rgba(217, 119, 6, 0.35)!important; }

            .dsh-bell:hover svg { animation: dsh-bell-ring 0.6s ease; }

            .dsh-qa { transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1); border: 1px solid rgba(229, 231, 235, 0.7); }
            .dsh-qa:hover { background: rgba(99, 102, 241, 0.05)!important; border-color: rgba(99, 102, 241, 0.35)!important; transform: translateY(-3px) scale(1.02); box-shadow: 0 20px 38px -12px rgba(99, 102, 241, 0.15); }
            .dsh-qa:hover svg { transform: translateY(-2px) rotate(5deg); transition: transform 0.2s ease; }
            
            .dsh-row { transition: background-color 0.2s; }
            .dsh-row:hover { background: rgba(99, 102, 241, 0.03)!important; border-radius: 8px; }
            .dsh-alert { transition: transform 0.2s, box-shadow 0.2s; }
            .dsh-alert:hover { transform: translateY(-2px); box-shadow: 0 8px 16px -4px rgba(0,0,0,0.06); cursor:pointer; }
          `}</style>

          {/* Decorative blurred background orbs */}
          <div style={{
            position: "absolute",
            top: -100,
            right: -100,
            width: 500,
            height: 500,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(99, 102, 241, 0.09) 0%, rgba(99, 102, 241, 0) 70%)",
            filter: "blur(50px)",
            pointerEvents: "none",
            zIndex: 0,
          }} />
          <div style={{
            position: "absolute",
            top: 300,
            left: -200,
            width: 600,
            height: 600,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(59, 130, 246, 0.08) 0%, rgba(59, 130, 246, 0) 70%)",
            filter: "blur(60px)",
            pointerEvents: "none",
            zIndex: 0,
          }} />

          <style>{`
            @keyframes dsh-pulse { 0%,100%{opacity:1} 50%{opacity:.45} }
            @keyframes dsh-shimmer { 0%{background-position:100% 50%} 100%{background-position:0 50%} }
            .dsh-kpi { text-decoration:none; color:inherit; display:block; z-index: 1; }
            .dsh-kpi>div { transition:box-shadow 0.3s cubic-bezier(0.16, 1, 0.3, 1), transform 0.3s cubic-bezier(0.16, 1, 0.3, 1), border-color 0.3s ease; }
            .dsh-kpi:hover>div { transform: translateY(-4px); }
            
            /* Glow Shadows on Hover */
            .dsh-kpi-revenue:hover>div { box-shadow: 0 20px 38px -10px rgba(16, 185, 129, 0.18), 0 4px 6px -2px rgba(16, 185, 129, 0.05)!important; border-color: rgba(16, 185, 129, 0.35)!important; }
            .dsh-kpi-admissions:hover>div { box-shadow: 0 20px 38px -10px rgba(37, 99, 235, 0.18), 0 4px 6px -2px rgba(37, 99, 235, 0.05)!important; border-color: rgba(37, 99, 235, 0.35)!important; }
            .dsh-kpi-attendance:hover>div { box-shadow: 0 20px 38px -10px rgba(2, 132, 199, 0.18), 0 4px 6px -2px rgba(2, 132, 199, 0.05)!important; border-color: rgba(2, 132, 199, 0.35)!important; }
            .dsh-kpi-exams:hover>div { box-shadow: 0 20px 38px -10px rgba(217, 119, 6, 0.18), 0 4px 6px -2px rgba(217, 119, 6, 0.05)!important; border-color: rgba(217, 119, 6, 0.35)!important; }

            .dsh-qa { transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1); border: 1px solid rgba(229, 231, 235, 0.7); }
            .dsh-qa:hover { transform: scale(1.05) translateY(-3px); }
            .dsh-qa svg { transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1); }
            .dsh-qa:hover svg { transform: translateY(-2px) rotate(6deg) scale(1.05); }

            /* Category specific hover effects */
            .dsh-qa-admissions:hover {
              background: rgba(59, 130, 246, 0.04)!important;
              border-color: rgba(59, 130, 246, 0.4)!important;
              box-shadow: 0 14px 28px -8px rgba(59, 130, 246, 0.25), 0 0 0 1px rgba(59, 130, 246, 0.1);
            }
            .dsh-qa-revenue:hover {
              background: rgba(34, 197, 94, 0.04)!important;
              border-color: rgba(34, 197, 94, 0.4)!important;
              box-shadow: 0 14px 28px -8px rgba(34, 197, 94, 0.25), 0 0 0 1px rgba(34, 197, 94, 0.1);
            }
            .dsh-qa-expenses:hover {
              background: rgba(239, 68, 68, 0.04)!important;
              border-color: rgba(239, 68, 68, 0.4)!important;
              box-shadow: 0 14px 28px -8px rgba(239, 68, 68, 0.25), 0 0 0 1px rgba(239, 68, 68, 0.1);
            }
            .dsh-qa-academic:hover {
              background: rgba(139, 92, 246, 0.04)!important;
              border-color: rgba(139, 92, 246, 0.4)!important;
              box-shadow: 0 14px 28px -8px rgba(139, 92, 246, 0.25), 0 0 0 1px rgba(139, 92, 246, 0.1);
            }
            .dsh-qa-attendance:hover {
              background: rgba(20, 184, 166, 0.04)!important;
              border-color: rgba(20, 184, 166, 0.4)!important;
              box-shadow: 0 14px 28px -8px rgba(20, 184, 166, 0.25), 0 0 0 1px rgba(20, 184, 166, 0.1);
            }
            .dsh-qa-exams:hover {
              background: rgba(245, 158, 11, 0.04)!important;
              border-color: rgba(245, 158, 11, 0.4)!important;
              box-shadow: 0 14px 28px -8px rgba(245, 158, 11, 0.25), 0 0 0 1px rgba(245, 158, 11, 0.1);
            }
            .dsh-qa-doubts:hover {
              background: rgba(99, 102, 241, 0.04)!important;
              border-color: rgba(99, 102, 241, 0.4)!important;
              box-shadow: 0 14px 28px -8px rgba(99, 102, 241, 0.25), 0 0 0 1px rgba(99, 102, 241, 0.1);
            }
            .dsh-qa-students:hover {
              background: rgba(236, 72, 153, 0.04)!important;
              border-color: rgba(236, 72, 153, 0.4)!important;
              box-shadow: 0 14px 28px -8px rgba(236, 72, 153, 0.25), 0 0 0 1px rgba(236, 72, 153, 0.1);
            }
            .dsh-row { transition: background-color 0.2s; }
            .dsh-row:hover { background: rgba(99, 102, 241, 0.03)!important; border-radius: 8px; }
            .dsh-alert { transition: transform 0.2s, box-shadow 0.2s; }
            .dsh-alert:hover { transform: translateY(-2px); box-shadow: 0 8px 16px -4px rgba(0,0,0,0.06); cursor:pointer; }
          `}</style>

          {/* === HEADER ROW ====================================================== */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 24,
              position: "relative",
              zIndex: 10,
            }}
          >
            <div>
              {/* <div style={{ fontSize: 10, fontWeight: 700, color: "#6366f1", letterSpacing: "0.1em", textTransform: "uppercase" }}>
                Academic Operations Management
              </div> */}
              {/* <h1
                style={{
                  fontSize: 24,
                  fontWeight: 800,
                  color: "#111827",
                  margin: "4px 0 0 0",
                  letterSpacing: "-0.02em",
                }}
              >
                Institution Console
              </h1> */}
            </div>

            {/* No Branch dropdown — the header's (NavbarOne) branch selection
                scopes this dashboard. */}
          </div>

          {/* === KPI ROW ======================================================== */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
              gap: 12,
              marginBottom: 12,
              position: "relative",
              zIndex: 2,
            }}
          >
            {/* 1 - Revenue (collected fees) */}
            <Link
              to="/Fee-Structure"
              className="dsh-kpi dsh-kpi-revenue"
              style={{
                textDecoration: "none",
                color: "inherit",
                display: "block",
              }}
            >
              <div style={{ ...card, background: "linear-gradient(135deg, rgba(240, 253, 244, 0.9) 0%, rgba(255, 255, 255, 0.95) 100%)", borderColor: "rgba(34, 197, 94, 0.25)" }}>
                <div style={{ padding: "16px 18px 16px" }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                    }}
                  >
                    <div style={lbl}>
                      REVENUE . {monthName.slice(0, 3).toUpperCase()}
                    </div>
                    <IconCircle bg="#e6f4ea" color="#137333" size={34}>
                      <span style={{ fontSize: 15, fontWeight: 700 }}>₹</span>
                    </IconCircle>
                  </div>
                  {loadingFee ? (
                    <div style={{ margin: "8px 0 14px" }}>
                      <Sk h={28} w="60%" />
                      <div style={{ height: 6 }} />
                      <Sk h={12} w="80%" />
                    </div>
                  ) : (
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
                      <div>
                        <div
                          style={{
                            fontSize: 28,
                            fontWeight: 700,
                            lineHeight: 1.1,
                            margin: "6px 0 4px",
                          }}
                        >
                          {fmtINR(collected)}
                        </div>
                        <div>
                          {collPct > 0 && <Chip text={`â†‘ ${collPct}% collected`} />}
                          <span style={{ fontSize: 11, color: "#6b7280" }}>
                            of {fmtINR(totalFeeTarget)} target
                          </span>
                        </div>
                      </div>
                      <div style={{ paddingBottom: 5, paddingRight: 4, zIndex: 1 }}>
                        <Sparkline data={feeData?.termWiseData?.map(t => t.collectedAmount) || [45, 52, 49, 63, 58, 67]} color="#22c55e" />
                      </div>
                    </div>
                  )}
                  <ProgressBar pct={collPct} color="#22c55e" />
                  <div style={{ position: "absolute", right: -15, bottom: -20, opacity: 0.05, color: "#22c55e", pointerEvents: "none", zIndex: 0 }}>
                    <svg width="100" height="100" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                      <circle cx="12" cy="12" r="10" />
                      <path d="M12 6v12M12 10a2.5 2.5 0 0 0 0 5h1.5a2.5 2.5 0 0 0 0-5H12" />
                    </svg>
                  </div>
                </div>
              </div>
            </Link>

            {/* 2 - New Admissions */}
            <Link
              to="/admissions-dashboard"
              className="dsh-kpi dsh-kpi-admissions"
              style={{
                textDecoration: "none",
                color: "inherit",
                display: "block",
              }}
            >
              <div style={{ ...card, background: "linear-gradient(135deg, rgba(239, 246, 255, 0.9) 0%, rgba(255, 255, 255, 0.95) 100%)", borderColor: "rgba(59, 130, 246, 0.25)" }}>
                <div style={{ padding: "16px 18px 16px" }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                    }}
                  >
                    <div style={lbl}>NEW ADM. . AY {fiscalYr}</div>
                    <IconCircle bg="#dbeafe" color="#2563eb" size={34}>
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2" />
                        <circle cx="9" cy="7" r="4" />
                        <line x1="19" y1="8" x2="19" y2="14" />
                        <line x1="22" y1="11" x2="16" y2="11" />
                      </svg>
                    </IconCircle>
                  </div>
                  {this.props.data?.loading ? (
                    <div style={{ margin: "8px 0 14px" }}>
                      <Sk h={28} w="60%" />
                      <div style={{ height: 6 }} />
                      <Sk h={12} w="80%" />
                    </div>
                  ) : (
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
                      <div>
                        <div
                          style={{
                            fontSize: 28,
                            fontWeight: 700,
                            lineHeight: 1.1,
                            margin: "6px 0 4px",
                          }}
                        >
                          {newAdmissions.toLocaleString()}{" "}
                          <span
                            style={{
                              fontSize: 16,
                              color: "#9ca3af",
                              fontWeight: 400,
                            }}
                          >
                            / {totalStudents.toLocaleString()}
                          </span>
                        </div>
                        <div>
                          <Chip
                            text={`${totalStudents - newAdmissions} existing`}
                          />
                          <span style={{ fontSize: 11, color: "#6b7280" }}>
                            total enrolled
                          </span>
                        </div>
                      </div>
                      <div style={{ paddingBottom: 5, paddingRight: 4, zIndex: 1 }}>
                        <Sparkline data={enrollmentData?.monthlyData?.map(m => m.count) || [10, 15, 12, 18, 22, 25]} color="#3b82f6" />
                      </div>
                    </div>
                  )}
                  <ProgressBar
                    pct={
                      totalStudents > 0
                        ? Math.round((newAdmissions / totalStudents) * 100)
                        : 0
                    }
                    color="#3b82f6"
                  />
                  <div style={{ position: "absolute", right: -15, bottom: -20, opacity: 0.05, color: "#3b82f6", pointerEvents: "none", zIndex: 0 }}>
                    <svg width="100" height="100" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
                      <circle cx="9" cy="7" r="4" />
                      <line x1="19" y1="8" x2="19" y2="14" />
                      <line x1="22" y1="11" x2="16" y2="11" />
                    </svg>
                  </div>
                </div>
              </div>
            </Link>

            {/* 3 - Attendance % (today) */}
            <Link
              to="/attendance-regularization"
              className="dsh-kpi dsh-kpi-attendance"
              style={{
                textDecoration: "none",
                color: "inherit",
                display: "block",
              }}
            >
              <div style={{ ...card, background: "linear-gradient(135deg, rgba(240, 253, 250, 0.9) 0%, rgba(255, 255, 255, 0.95) 100%)", borderColor: "rgba(20, 184, 166, 0.25)" }}>
                <div style={{ padding: "16px 18px 16px" }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                    }}
                  >
                    <div style={lbl}>ATTENDANCE . TODAY</div>
                    <IconCircle bg="#dcfce7" color="#16a34a" size={34}>
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M9 11l3 3L22 4" />
                        <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" />
                      </svg>
                    </IconCircle>
                  </div>
                  {loadingAttendance ? (
                    <div style={{ margin: "8px 0 14px" }}>
                      <Sk h={28} w="60%" />
                      <div style={{ height: 6 }} />
                      <Sk h={12} w="80%" />
                    </div>
                  ) : (
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", margin: "6px 0 8px" }}>
                      <div>
                        <div
                          style={{
                            fontSize: 28,
                            fontWeight: 700,
                            lineHeight: 1.1,
                            margin: "0 0 4px 0",
                          }}
                        >
                          {presentSt.toLocaleString()}
                          <span style={{ fontSize: 16, color: "#9ca3af", fontWeight: 400 }}> / {(sAtt.totalStudents || 0).toLocaleString()}</span>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4 }}>
                          <Sparkline data={[75, 82, 80, 85, 88, attPct]} color="#14b8a6" />
                          <span style={{ fontSize: 10, color: "#6b7280" }}>6d trend</span>
                        </div>
                      </div>
                      <div style={{ zIndex: 1, paddingRight: 4 }}>
                        <CircularGauge percentage={attPct} color="#14b8a6" />
                      </div>
                    </div>
                  )}
                  <div style={{ position: "absolute", right: -15, bottom: -20, opacity: 0.05, color: "#14b8a6", pointerEvents: "none", zIndex: 0 }}>
                    <svg width="100" height="100" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                      <line x1="16" y1="2" x2="16" y2="6" />
                      <line x1="8" y1="2" x2="8" y2="6" />
                      <line x1="3" y1="10" x2="21" y2="10" />
                      <polyline points="8 14 10 16 16 10" />
                    </svg>
                  </div>
                </div>
              </div>
            </Link>

            {/* 4 - Pass % */}
            <Link
              to="/ExamReport_tabs"
              className="dsh-kpi dsh-kpi-exams"
              style={{
                textDecoration: "none",
                color: "inherit",
                display: "block",
              }}
            >
              <div style={{ ...card, background: "linear-gradient(135deg, rgba(255, 251, 235, 0.9) 0%, rgba(255, 255, 255, 0.95) 100%)", borderColor: "rgba(245, 158, 11, 0.25)" }}>
                <div style={{ padding: "16px 18px 16px" }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                    }}
                  >
                    <div style={lbl}>PASS % . LATEST EXAM</div>
                    <IconCircle bg="#fef3c7" color="#d97706" size={34}>
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <circle cx="12" cy="8" r="6" />
                        <path d="M15.477 12.89L17 22l-5-3-5 3 1.523-9.11" />
                      </svg>
                    </IconCircle>
                  </div>
                  {loadingExams ? (
                    <div style={{ margin: "8px 0 14px" }}>
                      <Sk h={28} w="60%" />
                      <div style={{ height: 6 }} />
                      <Sk h={12} w="80%" />
                    </div>
                  ) : (
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", margin: "6px 0 8px" }}>
                      <div>
                        <div
                          style={{
                            fontSize: 28,
                            fontWeight: 700,
                            lineHeight: 1.1,
                            margin: "0 0 4px 0",
                          }}
                        >
                          {passRate > 0 ? `${passRate}` : "--"}
                          {passRate > 0 && <span style={{ fontSize: 16, color: "#9ca3af", fontWeight: 400 }}>% avg</span>}
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4 }}>
                          <Sparkline data={examTrendData?.map(e => e.passRate) || [65, 70, 72, 68, 75, passRate]} color="#f59e0b" />
                          <span style={{ fontSize: 10, color: "#6b7280" }}>trend</span>
                        </div>
                      </div>
                      <div style={{ zIndex: 1, paddingRight: 4 }}>
                        <CircularGauge percentage={passRate} color="#f59e0b" />
                      </div>
                    </div>
                  )}
                  <div style={{ position: "absolute", right: -15, bottom: -20, opacity: 0.05, color: "#f59e0b", pointerEvents: "none", zIndex: 0 }}>
                    <svg width="100" height="100" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                      <circle cx="12" cy="8" r="7" />
                      <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88" />
                    </svg>
                  </div>
                </div>
              </div>
            </Link>


          </div>

          {/* === ALERT STRIP ==================================================== */}
          {alerts.length > 0 && (
            <div
              style={{
                ...card,
                padding: "10px 16px",
                marginBottom: 12,
                display: "flex",
                alignItems: "center",
                gap: 10,
                overflowX: "auto",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  color: "#92400e",
                  fontSize: 12,
                  fontWeight: 600,
                  flexShrink: 0,
                }}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                  <line x1="12" y1="9" x2="12" y2="13" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
                Needs you today
              </div>
              <div
                style={{
                  width: 1,
                  height: 30,
                  background: "#e5e7eb",
                  flexShrink: 0,
                }}
              />
              {alerts.map((a, i) => (
                <div
                  key={i}
                  className="dsh-alert"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    background: a.bg,
                    borderRadius: 8,
                    padding: "6px 12px 6px 8px",
                    flexShrink: 0,
                  }}
                >
                  <div
                    style={{
                      width: 26,
                      height: 26,
                      borderRadius: 6,
                      background: a.iconBg,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 12,
                      color: a.iconColor,
                      flexShrink: 0,
                    }}
                  >
                    {a.icon}
                  </div>
                  <div>
                    <div
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: "#111827",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {a.title}
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        color: "#6b7280",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {a.sub}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* === MAIN MODULAR GRID ================================================ */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 12,
              marginTop: 12,
            }}
          >
            {/* -- ROW 1: Today at a Glance & Attendance ---------------------- */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(450px, 1fr))",
                gap: 12,
                alignItems: "stretch",
              }}
            >
              {this.renderTodayAtGlance(vals)}
              {this.renderAttendanceSummary(vals)}
            </div>

            {/* -- ROW 2: Fee Revenue & Expenses ------------------------------ */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(450px, 1fr))",
                gap: 12,
                alignItems: "stretch",
              }}
            >
              {this.renderRevenue(vals)}
              {this.renderExpenses(vals)}
            </div>

            {/* -- ROW 3: Academic Performance & Admission Funnel ------------- */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(450px, 1fr))",
                gap: 12,
                alignItems: "stretch",
              }}
            >
              {this.renderAcademicPerformance(vals)}
              {this.renderAdmissionFunnel(vals)}
            </div>

            {/* -- ROW 4: Quick Actions (Full Width) -------------------------- */}
            {this.renderQuickActions(vals)}
          </div>
          {/* END MAIN MODULAR GRID */}

          {/* === SEAT UTILIZATION MODAL ====================================== */}
          {showSeatModal && (() => {
            const grades = seatCapacity?.grades || [];
            const totalClasses = grades.length;
            const totalEnrolledAll = seatCapacity?.totalEnrolled || 0;
            const totalCapacityAll = seatCapacity?.totalCapacity || 0;
            const vacantSeats = totalCapacityAll - totalEnrolledAll;
            const avgClassSize = totalClasses > 0 ? Math.round(totalEnrolledAll / totalClasses) : 0;
            const overallUtil = Math.round(seatCapacity?.overallUtilization || 0);

            // group grades by base name: "10-A" -> "Grade 10", "Grade 10-A" -> "Grade 10"
            const groupMap = {};
            grades.forEach((g) => {
              const base = g.gradeName.replace(/[-\s][A-Za-z]$/, "").replace(/^Grade\s*/i, "").trim();
              const key = `Grade ${base}`;
              if (!groupMap[key]) groupMap[key] = [];
              groupMap[key].push(g);
            });
            const groups = Object.entries(groupMap);

            return (
              <div
                onClick={() => this.setState({ showSeatModal: false })}
                style={{
                  position: "fixed", inset: 0, zIndex: 9999,
                  background: "rgba(0,0,0,0.45)",
                  display: "flex", alignItems: "flex-start", justifyContent: "center",
                  paddingTop: 40, overflowY: "auto",
                }}
              >
                <div
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    background: "#eef0f4",
                    borderRadius: 16,
                    width: "min(820px, 95vw)",
                    marginBottom: 40,
                    boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
                    overflow: "hidden",
                  }}
                >
                  {/* Modal header */}
                  <div style={{
                    background: "#fff",
                    padding: "16px 20px",
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    borderBottom: "1px solid #f3f4f6",
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{
                        width: 34, height: 34, borderRadius: "50%",
                        background: "#ede9fe", color: "#7c3aed",
                        display: "flex", alignItems: "center", justifyContent: "center",
                      }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="3" y="3" width="7" height="7" rx="1" />
                          <rect x="14" y="3" width="7" height="7" rx="1" />
                          <rect x="3" y="14" width="7" height="7" rx="1" />
                          <rect x="14" y="14" width="7" height="7" rx="1" />
                        </svg>
                      </div>
                      <div>
                        <div style={{ fontSize: 15, fontWeight: 700, color: "#111827" }}>Seat Utilization</div>
                        <div style={{ fontSize: 11, color: "#9ca3af" }}>AY {fiscalYr} . all grades</div>
                      </div>
                    </div>
                    <button
                      onClick={() => this.setState({ showSeatModal: false })}
                      style={{
                        width: 30, height: 30, borderRadius: "50%", border: "none",
                        background: "#f3f4f6", cursor: "pointer", fontSize: 18,
                        color: "#6b7280", display: "flex", alignItems: "center", justifyContent: "center",
                      }}
                    >x</button>
                  </div>

                  <div style={{ padding: 16 }}>
                    {/* Summary stat cards */}
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10, marginBottom: 14 }}>
                      {[
                        { label: "Total Classes", value: totalClasses, icon: "🏫", bg: "#ede9fe", vc: "#7c3aed" },
                        { label: "Total Students", value: totalEnrolledAll.toLocaleString(), icon: "ðŸ‘¤", bg: "#dbeafe", vc: "#2563eb" },
                        { label: "Avg Class Size", value: avgClassSize, icon: "ðŸ“Š", bg: "#dcfce7", vc: "#059669" },
                        { label: "Vacant Seats", value: vacantSeats, icon: "💺", bg: "#fef3c7", vc: "#d97706" },
                      ].map((s, i) => (
                        <div key={i} style={{ background: "#fff", borderRadius: 10, padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                            <div style={{ fontSize: 10, fontWeight: 700, color: "#9ca3af", letterSpacing: "0.07em", textTransform: "uppercase" }}>{s.label}</div>
                            <div style={{ width: 28, height: 28, borderRadius: "50%", background: s.bg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}>{s.icon}</div>
                          </div>
                          <div style={{ fontSize: 26, fontWeight: 700, color: s.vc }}>{s.value}</div>
                        </div>
                      ))}
                    </div>

                    {/* Overall utilization bar */}
                    <div style={{ background: "#fff", borderRadius: 10, padding: "12px 16px", marginBottom: 14, boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                        <span style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>Overall utilization</span>
                        <span style={{ fontSize: 13, fontWeight: 700, color: pctColor(overallUtil) }}>{overallUtil}%</span>
                      </div>
                      <div style={{ height: 8, background: "#f3f4f6", borderRadius: 99, overflow: "hidden" }}>
                        <div style={{ height: "100%", width: `${Math.min(100, overallUtil)}%`, background: pctColor(overallUtil), borderRadius: 99, transition: "width 0.4s" }} />
                      </div>
                      <div style={{ display: "flex", gap: 16, marginTop: 8 }}>
                        {[["Enrolled", totalEnrolledAll, "#3b82f6"], ["Capacity", totalCapacityAll, "#6b7280"], ["Vacant", vacantSeats, "#f59e0b"]].map(([l, v, c]) => (
                          <span key={l} style={{ fontSize: 11, color: "#6b7280" }}>
                            <span style={{ fontWeight: 700, color: c }}>{(v || 0).toLocaleString()}</span> {l}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Grade-wise breakdown */}
                    {loadingSeatCapacity ? (
                      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        {[1, 2, 3].map(i => <div key={i} style={{ height: 80, borderRadius: 10, background: "linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 37%, #e5e7eb 63%)", backgroundSize: "400% 100%", animation: "dsh-shimmer 1.4s ease-in-out infinite" }} />)}
                      </div>
                    ) : grades.length === 0 ? (
                      <div style={{ textAlign: "center", padding: 40, color: "#9ca3af", fontSize: 13 }}>No seat capacity data available</div>
                    ) : (
                      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                        {groups.map(([groupName, sections]) => (
                          <div key={groupName} style={{ background: "#fff", borderRadius: 12, padding: 16, boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
                            <div style={{ fontSize: 13, fontWeight: 700, color: "#111827", marginBottom: 12 }}>{groupName}</div>
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(230px,1fr))", gap: 10 }}>
                              {sections.map((g) => {
                                const util = Math.round(g.utilizationPercentage || 0);
                                const color = pctColor(util);
                                return (
                                  <div key={g.classId} style={{ border: "1px solid #f3f4f6", borderRadius: 10, padding: "12px 14px" }}>
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                                      <span style={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>{g.gradeName}</span>
                                      <span style={{
                                        fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 20,
                                        background: util >= 90 ? "#fef3c7" : util >= 70 ? "#dcfce7" : "#f3f4f6",
                                        color: util >= 90 ? "#d97706" : util >= 70 ? "#059669" : "#6b7280",
                                      }}>{g.enrolledCount}/{g.totalCapacity}</span>
                                    </div>
                                    <div style={{ height: 6, background: "#f3f4f6", borderRadius: 99, overflow: "hidden", marginBottom: 6 }}>
                                      <div style={{ height: "100%", width: `${Math.min(100, util)}%`, background: color, borderRadius: 99 }} />
                                    </div>
                                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                                      <span style={{ fontSize: 11, color: "#6b7280" }}>{util}% full</span>
                                      <span style={{ fontSize: 11, color: "#6b7280" }}>{g.availableSeats} seats left</span>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Shown when a role without is_edit clicks any nav card/link — the redirect is
              blocked (see handleNavGuardCapture) and this pops instead. */}
          <AccessDeniedModal
            show={this.state.accessDeniedShow}
            onHide={this.guard.hideAccessDenied}
          />
        </div>
      );
    }
  }

  export default compose(
    withApollo,
  )(NewSchoolDashboardSection);
